import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useClerk, useUser } from "@clerk/clerk-react";
import {
  ClipboardCopy,
  Cpu,
  Download,
  ChevronRight,
  FileText,
  Paperclip,
  Send,
  RotateCcw,
  Eye,
  Info,
  Layers,
  Lightbulb,
  Loader2,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Users,
  Activity,
  Target,
  GitBranch,
  CheckCircle2,
  ArrowUpRight,
  BookOpen,
  Linkedin,
  Mail,
  type LucideIcon,
} from "lucide-react";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MultiSelect } from "@/components/ui/multi-select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { MagicBgPattern } from "@/components/shabanhr/bg-pattern";
import { cn } from "@/lib/utils";
import { ensureAnalytics, registerContext, trackEvent } from "@/lib/analytics";
import {
  defaultPrdTemplateId,
  prdTemplateMap,
  prdTemplates,
  type PrdTemplate,
  type PrdTemplateId,
  type PrdTemplateIcon,
} from "../../shared/prdTemplates";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Monitor, Smartphone, Code2, BarChart2, Store, ShoppingCart, Gamepad2, Workflow } from "lucide-react";

type GuidedFieldKey =
  | "productTagline"
  | "background"
  | "targetUsers"
  | "userProblems"
  | "goals"
  | "features"
  | "requirements"
  | "metrics"
  | "launchPlan"
  | "risks"
  | "openQuestions"
  | "techPreferences"
  | "backendServices"
  | "deploymentNotes";

type GuidedField = {
  key: GuidedFieldKey;
  title: string;
  prompt: string;
  placeholder: string;
};

type FormState = {
  productName: string;
  productTagline: string;
  background: string;
  targetUsers: string;
  userProblems: string;
  goals: string;
  features: string;
  requirements: string;
  metrics: string;
  launchPlan: string;
  risks: string;
  openQuestions: string;
  techPreferences: string;
  backendServices: string;
  deploymentNotes: string;
};

const MONTHLY_EXPLORER_LIMIT = 3;

const GENERATION_STEPS = [
  {
    title: "Synthesising your brief into a PRD",
    description: "Mapping product context, user pain, and success metrics into a shared framework.",
  },
  {
    title: "Charting user journeys",
    description: "Transforming your answers into end-to-end flows and tangible scenarios.",
  },
  {
    title: "Locking feature priorities",
    description: "Balancing goals, risks, and scope to anchor implementation-ready requirements.",
  },
  {
    title: "Defining guardrails",
    description: "Capturing non-functional expectations, integrations, and operational notes.",
  },
  {
    title: "Packaging the delivery plan",
    description: "Outlining rollout, dependencies, and acceptance criteria for the build team.",
  },
] as const;

type InteractionMode = "chat" | "manual";

type ChatMessageMeta = {
  title?: string;
  body?: string;
  bulletPoints?: string[];
  buttonLabel?: string;
  secondaryLabel?: string;
};

type ChatMessage = {
  role: "assistant" | "user";
  content: string;
  variant?: "default" | "outline-ready";
  meta?: ChatMessageMeta;
};

type AttachmentSource = "llama-parse" | "inline-text";

type OutlineHistoryEntry = {
  versionId: string;
  updatedAt: number;
  summary?: string;
  updatedKeys?: string[];
};

type BootstrapResponse = {
  summary?: string;
  fields?: Record<string, unknown>;
  updatedFields?: Record<string, unknown>;
  mergedFields?: Record<string, unknown>;
  outlineVersion?: string;
  updatedKeys?: string[];
  history?: OutlineHistoryEntry[];
  mode?: "bootstrap" | "adjust";
  usage?: {
    promptTokens?: number;
    completionTokens?: number;
    totalTokens?: number;
  };
  error?: string;
  updatedAt?: number;
};

const INITIAL_CHAT_MESSAGES: ChatMessage[] = [
  {
    role: "assistant",
    content:
      "Tell me about the product or feature you need a PRD for. Share who it’s for, the problem it solves, and any must-have capabilities.",
  },
];

const MIN_CHAT_CHARACTERS = 20;
const MAX_ATTACHMENT_SIZE_BYTES = 8 * 1024 * 1024; // 8MB
const MAX_ATTACHMENT_CHARS = 4000;
const SUPPORTED_ATTACHMENT_EXTENSIONS = [
  "md",
  "markdown",
  "txt",
  "json",
  "pdf",
  "doc",
  "docx",
  "ppt",
  "pptx",
  "csv",
  "html",
  "htm",
  "rtf",
];
const GUIDED_FIELDS: GuidedField[] = [
  {
    key: "productTagline",
    title: "Vision & Positioning",
    prompt: "Give a one-liner that explains what this product is and why it matters.",
    placeholder: "Example: \"CodeRide PRD Generator helps startups turn product ideas into structured specs in minutes.\"",
  },
  {
    key: "background",
    title: "Context & Background",
    prompt: "Share the current situation, business context, or triggering insight that led to this project.",
    placeholder: "Example: \"We keep seeing teams skip the PRD phase because it feels slow. Missing context creates churn in CodeRide builds.\"",
  },
  {
    key: "targetUsers",
    title: "Target Users",
    prompt: "Describe the customer segments, personas, or stakeholders who benefit from this product.",
    placeholder: "Example: \"Seed-stage founders, product-minded engineers, and PMs who need ready-made documentation.\"",
  },
  {
    key: "userProblems",
    title: "Problems To Solve",
    prompt: "List the core pain points or jobs-to-be-done you are addressing.",
    placeholder: "Example: \"Hard to structure a project brief\", \"Need to align engineering and marketing quickly\"",
  },
  {
    key: "goals",
    title: "Goals & Outcomes",
    prompt: "Define success: business goals, user outcomes, or acceptance criteria.",
    placeholder: "Example: \"Ship a shareable PRD draft in <10 minutes\", \"Drive more qualified CodeRide signups\"",
  },
  {
    key: "features",
    title: "Key Features",
    prompt: "List the must-have capabilities or sections the product must include.",
    placeholder: "Example: \"Guided questionnaire\", \"AI-assisted summaries\", \"Markdown export\"",
  },
  {
    key: "requirements",
    title: "Requirements & Constraints",
    prompt: "Capture functional requirements, edge cases, and non-functional needs like performance or compliance.",
    placeholder: "Example: \"Works without login for marketing preview\", \"Export must be Markdown only in v1\"",
  },
  {
    key: "metrics",
    title: "Success Metrics",
    prompt: "Identify the quantitative signals you will track post-launch.",
    placeholder: "Example: \"% of CodeRide users who generate PRDs\", \"# of qualified leads captured per week\"",
  },
  {
    key: "launchPlan",
    title: "Launch Plan",
    prompt: "Outline milestones, owners, and GTM notes.",
    placeholder: "Example: \"Beta in April\", \"Add CTA on pricing page\", \"Publish SEO pillar article\"",
  },
  {
    key: "risks",
    title: "Risks & Mitigations",
    prompt: "Call out known risks, dependencies, or trade-offs.",
    placeholder: "Example: \"Low adoption if export feels generic\", \"Mitigation: integrate CodeRide templates\"",
  },
  {
    key: "openQuestions",
    title: "Open Questions",
    prompt: "List any unresolved decisions or research needed.",
    placeholder: "Example: \"Do we gate advanced templates for Pro?\", \"How do we surface lead capture?\"",
  },
  {
    key: "techPreferences",
    title: "Preferred Frontend Stack",
    prompt: "Pick the primary UI stack you want to use. Choose \"Let AI decide\" if you want a suggested stack instead.",
    placeholder: "Example: React + TypeScript + Tailwind",
  },
  {
    key: "backendServices",
    title: "Backend & Services",
    prompt: "List backend services, databases, or APIs involved. Mention if you want the AI to recommend options.",
    placeholder: "Example: Supabase auth + Postgres, Cloudflare Workers, SendGrid email",
  },
  {
    key: "deploymentNotes",
    title: "Deployment & Infrastructure Notes",
    prompt: "Share deployment targets, observability or compliance needs.",
    placeholder: "Example: Deploy on Cloudflare Pages, EU data residency, use Logflare monitoring",
  },
];

const WHY_DOCS_POINTS: Array<{ title: string; copy: string; icon: LucideIcon; chipClass: string }> = [
  {
    title: "Stay in flow",
    copy: "Real-time feedback loops keep you coding while quality gates run in the background.",
    icon: Activity,
    chipClass: "border-[#63d7ff]/40 bg-[#63d7ff]/15 text-[#63d7ff]",
  },
  {
    title: "Automate guardrails",
    copy: "Documentation feeds security scanning, regression tests, and linting so issues surface instantly.",
    icon: ShieldCheck,
    chipClass: "border-[#5eead4]/40 bg-[#5eead4]/15 text-[#5eead4]",
  },
  {
    title: "Protect scope",
    copy: "Explicit specs preserve creative momentum without letting requirements drift.",
    icon: Target,
    chipClass: "border-[#7867EE]/40 bg-[#7867EE]/15 text-[#c2b5ff]",
  },
  {
    title: "Cleaner merges",
    copy: "AI review thrives on team norms, producing diffs reviewers trust and ship quickly.",
    icon: GitBranch,
    chipClass: "border-white/15 bg-white/10 text-white/80",
  },
];

const AI_SEARCH_KEYWORDS = [
  "what is a prd",
  "prd template",
  "prd document",
  "prd software",
  "ai prd generator",
] as const;

const AI_TOOL_LOGOS = [
  {
    name: "Cursor",
    logo:
      "https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/cursor.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2N1cnNvci5wbmciLCJpYXQiOjE3NDQ4MTM4OTAsImV4cCI6MTc3NjM0OTg5MH0.pTP_tJOiJPw6kkfgsIP3w2YuIUPijpX1B_iDBxe90Qc",
    alt: "Cursor logo",
  },
  {
    name: "Claude",
    logo:
      "https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/claude-color.svg?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2NsYXVkZS1jb2xvci5zdmciLCJpYXQiOjE3NDQ4MTUyMzMsImV4cCI6MTc3NjM1MTIzM30.zgWIB1ohcJBU33U-LNsYLAd79tnI9PhEw1Auw-s3Rpc",
    alt: "Claude logo",
  },
  {
    name: "CodeRide",
    logo:
      "https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/coderide_logo_color-square.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9jOTljZjI2OC04OTEzLTRhYjktYmI4YS0yMTE1MjQ2YzRjNjQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2NvZGVyaWRlX2xvZ29fY29sb3Itc3F1YXJlLnBuZyIsImlhdCI6MTc2Mjg3MzI4MSwiZXhwIjo0ODg0OTM3MjgxfQ.BcwXVyoym6bTy5NEI8iHYwzEcj3EKL9xufeRwsaGUYA",
    alt: "CodeRide logo",
  },
  {
    name: "Windsurf",
    logo:
      "https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/windsurf-white-symbol.svg?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9jOTljZjI2OC04OTEzLTRhYjktYmI4YS0yMTE1MjQ2YzRjNjQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL3dpbmRzdXJmLXdoaXRlLXN5bWJvbC5zdmciLCJpYXQiOjE3NjI5MzY4MDYsImV4cCI6MjgwMzYyNDgwNn0.aCXaUgSTlGVLvgJjPFWqLT0jOaNMFCax3vE42IXWjJg",
    alt: "Windsurf logo",
  },
  {
    name: "VS Code",
    logo:
      "https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/Visual_Studio_Code_1.35_icon.svg.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL1Zpc3VhbF9TdHVkaW9fQ29kZV8xLjM1X2ljb24uc3ZnLnBuZyIsImlhdCI6MTc0NDgwMTgwNywiZXhwIjoxNzc2MzM3ODA3fQ.gK87ziBf8qZ-0kdaIFgiiK0KnBi0dUOkShV0ojhTRlE",
    alt: "VS Code logo",
  },
  {
    name: "Cline",
    logo:
      "https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/cline%20logo_favicon-256x256.png?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2NsaW5lIGxvZ29fZmF2aWNvbi0yNTZ4MjU2LnBuZyIsImlhdCI6MTc0NDgwMjA3NCwiZXhwIjoxNzc2MzM4MDc0fQ.6qE_SdHQu6zh5suwojsSSCbs7uXBg-STY9CNmM1KZ5A",
    alt: "Cline logo",
  },
  {
    name: "GitHub Copilot",
    logo:
      "https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/githubcopilot.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9jOTljZjI2OC04OTEzLTRhYjktYmI4YS0yMTE1MjQ2YzRjNjQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2dpdGh1YmNvcGlsb3QucG5nIiwiaWF0IjoxNzYyOTM2OTg2LCJleHAiOjI4MDM2MjQ5ODZ9.9t_zPBynoVqltoiskVV9RjcdC7Llnkw_KficZLe-KfE",
    alt: "GitHub Copilot logo",
  },
] as const;

const PRD_VISIBILITY_SUBHEADS = [
  {
    title: "How AI-generated PRDs improve coding precision",
    copy:
      "Coders ship cleaner diffs when AI assistants inherit a living spec. It trims prompt churn, locks acceptance criteria, and feeds tests that catch regressions before review.",
  },
  {
    title: "From documentation to deployable tasks",
    copy:
      "CodeRide snapshots each section of your PRD into actionable tasks so Cursor, Claude, or Windsurf can pick up the next story with full context preserved.",
  },
  {
    title: "Why AI product teams start with structured specs",
    copy:
      "High-velocity teams anchor vibe coding in research-backed templates. They keep product, design, and agents aligned without slowing down experimentation.",
  },
] as const;

const FAQ_CATEGORIES = [
  { value: "general", label: "General", icon: Info, chipClass: "border-white/20 bg-white/5 text-white" },
  { value: "workflow", label: "Integrations & Workflow", icon: Workflow, chipClass: "border-[#63d7ff]/30 bg-[#63d7ff]/10 text-[#63d7ff]" },
  { value: "plans", label: "Plans & Access", icon: Layers, chipClass: "border-[#7867EE]/30 bg-[#7867EE]/10 text-[#c2b5ff]" },
  { value: "technical", label: "Trust & Data", icon: ShieldCheck, chipClass: "border-[#5eead4]/30 bg-[#5eead4]/10 text-[#5eead4]" },
] as const;

type FaqCategoryValue = (typeof FAQ_CATEGORIES)[number]["value"];

type FaqItem = {
  question: string;
  answer: string;
  category: FaqCategoryValue;
  cta?: { label: string; href: string };
};

const AI_FAQ_ITEMS: FaqItem[] = [
  {
    question: "What is an AI PRD Generator?",
    answer:
      "An AI PRD Generator creates a structured Product Requirements Document using AI. It turns a short product brief or uploaded file into a full PRD your team and AI copilots—like Claude Code or Cursor—can use as shared context.",
    category: "general",
  },
  {
    question: "What is a Product Requirements Document (PRD)?",
    answer:
      "A PRD defines the problem you’re solving, who it’s for, and what success looks like. In AI-assisted development, it acts as the source of truth that gives coding agents persistent understanding of your project.",
    category: "general",
  },
  {
    question: "Who is the PRD Generator built for?",
    answer:
      "It’s designed for AI product teams, vibe coders, and solo builders who need to document intent before sending work to Cursor, Claude, Windsurf, or autonomous agents.",
    category: "general",
  },
  {
    question: "How does CodeRide integrate with Claude Code and Cursor?",
    answer:
      "Each PRD generated in CodeRide follows an MCP-based structure that Claude Code, Cursor, and Windsurf can load as context, so AI tools remember your goals between sessions.",
    category: "workflow",
    cta: { label: "Try it now — Generate a free PRD", href: "#prd-wizard" },
  },
  {
    question: "What is MCP and why does it matter?",
    answer:
      "Model Context Protocol keeps AI tools and people synchronized. CodeRide uses MCP to preserve project memory between chats and tasks, eliminating context resets for autonomous agents.",
    category: "workflow",
  },
  {
    question: "How does CodeRide turn my description into a structured PRD?",
    answer:
      "Share a quick brief or upload docs and the assistant maps personas, problems, and requirements into every section so you can review, edit, and export immediately.",
    category: "workflow",
  },
  {
    question: "Can I export my PRD to other tools?",
    answer:
      "Yes. Copy or download Markdown and import it into Notion, Linear, GitHub Issues, or your docs workflow—every export stays editable.",
    category: "workflow",
  },
  {
    question: "Is the PRD Generator really free?",
    answer:
      "Yes. Explorer plans include up to three PRDs per month at no cost, and upgrading unlocks unlimited PRDs plus workflow automation.",
    category: "plans",
  },
  {
    question: "Does CodeRide store or share my PRDs?",
    answer:
      "Your PRDs remain scoped to your workspace. CodeRide never sells data, and files are removed when you delete them from the project.",
    category: "technical",
  },
  {
    question: "What happens to files I upload with LlamaParse?",
    answer:
      "Uploads are parsed to extract context for the PRD, then they remain tied to your workspace only. Remove the attachment and the source file is deleted from the project.",
    category: "technical",
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: AI_FAQ_ITEMS.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
};

const FIELD_LOOKUP = GUIDED_FIELDS.reduce<Record<GuidedFieldKey, GuidedField>>((acc, field) => {
  acc[field.key] = field;
  return acc;
}, {} as Record<GuidedFieldKey, GuidedField>);

const isGuidedFieldKey = (key: string): key is GuidedFieldKey =>
  key !== "productName" && key in FIELD_LOOKUP;

const resolveFormFieldLabel = (key: string): string | null => {
  if (key === "productName") {
    return "Product name";
  }
  if (isGuidedFieldKey(key)) {
    return FIELD_LOOKUP[key]?.title ?? null;
  }
  return null;
};

const resolveGroupForField = (key: string) => {
  if (key === "productName") {
    return FORM_GROUPS[0].value;
  }
  if (isGuidedFieldKey(key)) {
    return FIELD_GROUP_LOOKUP[key];
  }
  return undefined;
};

const AI_DECIDE_LABEL = "Let AI decide";

const TECH_OPTIONS = [
  {
    value: AI_DECIDE_LABEL,
    label: AI_DECIDE_LABEL,
    helper: "AI will propose a stack that suits the goals and constraints you entered.",
  },
  {
    value: "React + TypeScript + Tailwind",
    label: "React + TypeScript",
    helper: "SPA with Vite + Tailwind CSS.",
  },
  {
    value: "Next.js + TypeScript",
    label: "Next.js + TypeScript",
    helper: "React SSR/SSG via Next.js.",
  },
  {
    value: "SvelteKit",
    label: "SvelteKit",
    helper: "Lightweight Svelte app with server routes.",
  },
  {
    value: "Vue 3 + Vite",
    label: "Vue 3",
    helper: "Component-driven Vue SPA via Vite.",
  },
];

const BACKEND_OPTIONS = [
  { value: "Supabase", label: "Supabase" },
  { value: "Cloudflare Workers", label: "Cloudflare Workers" },
  { value: "Firebase", label: "Firebase" },
  { value: "Node.js/Express", label: "Node.js / Express" },
  { value: "Prisma + Postgres", label: "Prisma + Postgres" },
  { value: "Planetscale", label: "PlanetScale" },
  { value: "Hasura", label: "Hasura" },
  { value: "Stripe", label: "Stripe" },
  { value: "SendGrid", label: "SendGrid" },
  { value: "Other", label: "Other" },
];

type FormGroup = {
  value: "vision" | "audience" | "scope" | "risks" | "tech";
  label: string;
  kicker: string;
  description: string;
  icon: LucideIcon;
  fields: GuidedFieldKey[];
};

const FORM_GROUPS: FormGroup[] = [
  {
    value: "vision",
    label: "Vision",
    kicker: "Step 1",
    description: "Set the high-level story so collaborators understand why this product exists.",
    icon: Eye,
    fields: ["productTagline", "background", "goals"],
  },
  {
    value: "audience",
    label: "Audience",
    kicker: "Step 2",
    description: "Capture who you are building for and the problems they need solved.",
    icon: Users,
    fields: ["targetUsers", "userProblems", "metrics"],
  },
  {
    value: "scope",
    label: "Scope",
    kicker: "Step 3",
    description: "Define what will ship, how it behaves, and how you will launch it.",
    icon: Layers,
    fields: ["features", "requirements", "launchPlan"],
  },
  {
    value: "risks",
    label: "Risks",
    kicker: "Step 4",
    description: "Call out uncertainties so engineering and stakeholders can respond early.",
    icon: ShieldAlert,
    fields: ["risks", "openQuestions"],
  },
  {
    value: "tech",
    label: "Tech Stack",
    kicker: "Step 5",
    description: "Choose the implementation stack or let the AI recommend one automatically.",
    icon: Cpu,
    fields: ["techPreferences", "backendServices", "deploymentNotes"],
  },
];

const FIELD_GROUP_LOOKUP = FORM_GROUPS.reduce<Record<GuidedFieldKey, (typeof FORM_GROUPS)[number]["value"]>>(
  (acc, group) => {
    group.fields.forEach((fieldKey) => {
      acc[fieldKey] = group.value;
    });
    return acc;
  },
  {} as Record<GuidedFieldKey, (typeof FORM_GROUPS)[number]["value"]>,
);

const HERO_PREVIEW_SNIPPET = [
  "## Vision",
  "- Launch a self-serve analytics hub so founders monitor growth KPIs in real time.",
  "",
  "## Target Users",
  "- Seed-stage founders building with CodeRide",
  "- Delivery leads who track adoption",
  "",
  "## Success Metrics",
  "- Activation rate above 70% within 14 days",
  "- Weekly active dashboards > 250 workspaces",
  "",
  "## Core Features",
  "- Workspace onboarding with progressive profiling",
  "- Metrics canvas with saved views & shareable links",
  "- Governance controls for roles & permissions",
].join("\n");

const DEFAULT_FORM: FormState = {
  productName: "",
  productTagline: "",
  background: "",
  targetUsers: "",
  userProblems: "",
  goals: "",
  features: "",
  requirements: "",
  metrics: "",
  launchPlan: "",
  risks: "",
  openQuestions: "",
  techPreferences: "Let AI decide",
  backendServices: "",
  deploymentNotes: "",
};

const PRODUCT_NAME_LIMIT = 200;
const FORM_KEYS = Object.keys(DEFAULT_FORM) as (keyof FormState)[];

const PLAN_BADGE_KEYS: Record<string, string> = {
  explorer: "explorer",
  creator: "creator",
  pro: "pro",
  "early adopter": "early-adopter",
};

const TEMPLATE_STORAGE_KEY = "coderide-prd-template";

// Draft persistence storage keys
const DRAFT_STORAGE_KEY = "coderide-prd-draft";
const SESSION_STORAGE_KEY = "coderide-prd-session";
const DRAFT_EXPIRY_DAYS = 7;

type PersistedDraft = {
  form: FormState;
  chatMessages: ChatMessage[];
  markdown: string;
  templateId: PrdTemplateId;
  interactionMode: InteractionMode;
  savedAt: number;
};

const saveDraft = (draft: Omit<PersistedDraft, "savedAt">) => {
  if (typeof window === "undefined") return;
  try {
    const data: PersistedDraft = { ...draft, savedAt: Date.now() };
    window.localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn("[PRD] Failed to save draft:", error);
  }
};

const loadDraft = (): PersistedDraft | null => {
  if (typeof window === "undefined") return null;
  try {
    const stored = window.localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!stored) return null;
    const data = JSON.parse(stored) as PersistedDraft;
    // Check expiry
    const expiryMs = DRAFT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    if (Date.now() - data.savedAt > expiryMs) {
      window.localStorage.removeItem(DRAFT_STORAGE_KEY);
      return null;
    }
    return data;
  } catch (error) {
    console.warn("[PRD] Failed to load draft:", error);
    return null;
  }
};

const clearDraft = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch (error) {
    console.warn("[PRD] Failed to clear draft:", error);
  }
};

const getOrCreateSessionId = (): string => {
  if (typeof window === "undefined") {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
  try {
    const stored = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) return stored;
    const newId = crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    window.localStorage.setItem(SESSION_STORAGE_KEY, newId);
    return newId;
  } catch {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
};

// Share credits storage
const SHARE_CREDITS_KEY = "coderide-share-credits";
type SharePlatform = "x" | "linkedin" | "bluesky" | "email";
type ShareCredits = Partial<Record<SharePlatform, number>>; // timestamp of when claimed

const getShareCredits = (): ShareCredits => {
  if (typeof window === "undefined") return {};
  try {
    const stored = window.localStorage.getItem(SHARE_CREDITS_KEY);
    return stored ? (JSON.parse(stored) as ShareCredits) : {};
  } catch {
    return {};
  }
};

const claimShareCredit = (platform: SharePlatform): boolean => {
  if (typeof window === "undefined") return false;
  try {
    const credits = getShareCredits();
    // Check if already claimed this month
    const claimed = credits[platform];
    if (claimed) {
      const claimedDate = new Date(claimed);
      const now = new Date();
      if (claimedDate.getMonth() === now.getMonth() && claimedDate.getFullYear() === now.getFullYear()) {
        return false; // Already claimed this month
      }
    }
    credits[platform] = Date.now();
    window.localStorage.setItem(SHARE_CREDITS_KEY, JSON.stringify(credits));
    return true;
  } catch {
    return false;
  }
};

const countShareCredits = (): number => {
  const credits = getShareCredits();
  const now = new Date();
  return Object.values(credits).filter((timestamp) => {
    if (!timestamp) return false;
    const date = new Date(timestamp);
    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  }).length;
};

const templateIconMap: Record<PrdTemplateIcon, LucideIcon> = {
  monitor: Monitor,
  smartphone: Smartphone,
  code: Code2,
  chart: BarChart2,
  store: Store,
  cart: ShoppingCart,
  gamepad: Gamepad2,
  workflow: Workflow,
  file: FileText,
};

function resolvePlanBadge(plan: string) {
  const normalized = plan.trim().toLowerCase();
  const match = Object.keys(PLAN_BADGE_KEYS).find((key) => normalized.includes(key));
  return PLAN_BADGE_KEYS[match ?? "explorer"];
}

function normalisePlan(rawPlan: unknown): string {
  if (typeof rawPlan === "string" && rawPlan.trim().length > 0) {
    return rawPlan.trim();
  }
  return "Explorer";
}

function isExplorerPlan(plan: string) {
  const lower = plan.toLowerCase();
  return lower.includes("explorer") || lower.includes("free") || lower.includes("trial");
}

function slugify(input: string) {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

const sanitizeBootstrapFields = (fields: Record<string, unknown> | undefined): Partial<FormState> => {
  if (!fields || typeof fields !== "object") return {};

  const result: Partial<FormState> = {};
  FORM_KEYS.forEach((key) => {
    const rawValue = (fields as Record<string, unknown>)[key];
    if (typeof rawValue === "string") {
      result[key] = rawValue.trim();
    }
  });

  return result;
};

function toBulletList(value: string): string {
  const items = value
    .split(/\n+/)
    .map((line) => line.replace(/^[-*]\s*/, "").trim())
    .filter(Boolean);

  if (items.length === 0) {
    return "";
  }

  return items.map((item) => `- ${item}`).join("\n");
}

function sectionMarkdown(title: string, content: string, opts: { list?: boolean } = {}): string {
  const trimmed = content.trim();
  if (!trimmed) {
    return "";
  }

  const body = opts.list ? toBulletList(trimmed) : trimmed;
  if (!body) {
    return "";
  }

  return `## ${title}\n\n${body}\n\n`;
}

function buildMarkdown(form: FormState, ownerEmail?: string | null): string {
  const title = form.productName.trim() || "Product Requirements Document";
  const lines: string[] = [`# ${title}`, ""];

  if (form.productTagline.trim()) {
    lines.push(`_${form.productTagline.trim()}_`, "");
  }

  if (ownerEmail) {
    lines.push(`- **Document Owner:** ${ownerEmail}`, "");
  }

  const techLines = [
    form.techPreferences && form.techPreferences !== AI_DECIDE_LABEL
      ? `Frontend stack: ${form.techPreferences}`
      : `Frontend stack: ${AI_DECIDE_LABEL}`,
    form.backendServices ? `Backend & services: ${form.backendServices}` : "",
    form.deploymentNotes ? `Deployment notes: ${form.deploymentNotes}` : "",
  ]
    .filter(Boolean)
    .join("\n");

  lines.push(
    sectionMarkdown("Background", form.background),
    sectionMarkdown("Target Users", form.targetUsers, { list: true }),
    sectionMarkdown("Problems To Solve", form.userProblems, { list: true }),
    sectionMarkdown("Goals & Success Criteria", form.goals, { list: true }),
    sectionMarkdown("Key Features", form.features, { list: true }),
    sectionMarkdown("Requirements", form.requirements, { list: true }),
    sectionMarkdown("Success Metrics", form.metrics, { list: true }),
    sectionMarkdown("Launch Plan", form.launchPlan, { list: true }),
    sectionMarkdown("Risks & Dependencies", form.risks, { list: true }),
    sectionMarkdown("Open Questions", form.openQuestions, { list: true }),
    sectionMarkdown("Tech Stack & Infrastructure", techLines, { list: true }),
  );

  lines.push(
    "---",
    "",
    "_Generated with the CodeRide PRD Generator. Refine this draft inside CodeRide to keep implementation aligned._",
  );

  return lines.filter(Boolean).join("\n");
}

const PRDGenerator = () => {
  const { user, isSignedIn } = useUser();
  const { openSignIn, openSignUp } = useClerk();
  const { toast } = useToast();

  // Get or create a persistent session ID for tracking and draft association
  const sessionId = useMemo(() => getOrCreateSessionId(), []);
  const hasMarkedActiveRef = useRef(false);
  const hasTrackedPageViewRef = useRef(false);

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [markdown, setMarkdown] = useState<string>("");
  const [lastGeneratedAt, setLastGeneratedAt] = useState<Date | null>(null);
  const [usageCount, setUsageCount] = useState<number>(0);
  const [shareBonusCredits, setShareBonusCredits] = useState<number>(() => countShareCredits());
  const [activeTab, setActiveTab] = useState<(typeof FORM_GROUPS)[number]["value"]>(FORM_GROUPS[0].value);
  const [isGenerating, setIsGenerating] = useState(false);
  const [interactionMode, setInteractionMode] = useState<InteractionMode>("chat");
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>(INITIAL_CHAT_MESSAGES);
  const [templateId, setTemplateId] = useState<PrdTemplateId>(defaultPrdTemplateId);
  const [chatInput, setChatInput] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [attachmentName, setAttachmentName] = useState<string | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<string | null>(null);
  const [attachmentSource, setAttachmentSource] = useState<AttachmentSource | null>(null);
  const [attachmentFormat, setAttachmentFormat] = useState<string | null>(null);
  const [attachmentTruncated, setAttachmentTruncated] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [isAttachmentUploading, setIsAttachmentUploading] = useState(false);
  const [outlineVersion, setOutlineVersion] = useState<string | null>(null);
  const [outlineHistory, setOutlineHistory] = useState<OutlineHistoryEntry[]>([]);
  const [recentlyUpdatedKeys, setRecentlyUpdatedKeys] = useState<Array<GuidedFieldKey | "productName">>([]);
  const [outlineHighlight, setOutlineHighlight] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [activeFaqCategory, setActiveFaqCategory] = useState<FaqCategoryValue>("general");
  const elapsedTimeRef = useRef<number | null>(null);
  const chatListRef = useRef<HTMLDivElement | null>(null);
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);
  const chatInputRef = useRef<HTMLTextAreaElement | null>(null);
  const manualOutlineRef = useRef<HTMLDivElement | null>(null);
  const outlineHighlightTimeoutRef = useRef<number | null>(null);
  const markdownSectionRef = useRef<HTMLDivElement | null>(null);
  const draftRestoredRef = useRef(false);
  const saveTimeoutRef = useRef<number | null>(null);
  const activeTemplate = prdTemplateMap[templateId] ?? prdTemplateMap[defaultPrdTemplateId];
  const ActiveTemplateIcon = templateIconMap[activeTemplate.icon];
  const activeLoadingStep = GENERATION_STEPS[loadingStepIndex] ?? GENERATION_STEPS[0];
  const backendSelected = useMemo(
    () =>
      form.backendServices
        .split(",")
        .map((entry) => entry.trim())
        .filter(Boolean),
    [form.backendServices],
  );
  const activeFaqCategoryMeta = FAQ_CATEGORIES.find((category) => category.value === activeFaqCategory) ?? FAQ_CATEGORIES[0];
  const visibleFaqItems = useMemo(() => {
    const scoped = AI_FAQ_ITEMS.filter((item) => item.category === activeFaqCategory);
    return scoped.length ? scoped : AI_FAQ_ITEMS;
  }, [activeFaqCategory]);
  const focusManualOutline = useCallback(() => {
    if (typeof window === "undefined") return;
    window.setTimeout(() => {
      manualOutlineRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      if (outlineHighlightTimeoutRef.current) {
        window.clearTimeout(outlineHighlightTimeoutRef.current);
      }
      setOutlineHighlight(true);
      outlineHighlightTimeoutRef.current = window.setTimeout(() => {
        setOutlineHighlight(false);
        outlineHighlightTimeoutRef.current = null;
      }, 2200);
    }, 120);
  }, []);

  const handleBackendChange = (values: string[]) => {
    handleUpdateField("backendServices", values.join(", "));
  };
  const handleTechSelect = (value: string) => {
    handleUpdateField("techPreferences", value);
  };
  const handleHistoryChipClick = useCallback(
    (fieldKey: string) => {
      const targetTab = resolveGroupForField(fieldKey);
      setInteractionMode("manual");
      if (targetTab) {
        setActiveTab(targetTab);
      }
      focusManualOutline();
      if (typeof window !== "undefined") {
        window.setTimeout(() => {
          const elementId = fieldKey === "productName" ? "productName" : `field-${fieldKey}`;
          const targetElement = document.getElementById(elementId);
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
            if ("focus" in targetElement) {
              (targetElement as HTMLElement).focus();
            }
          }
        }, 200);
      }
    },
    [focusManualOutline],
  );

  const handleOutlineReviewClick = () => {
    setInteractionMode("manual");
    setActiveTab(FORM_GROUPS[0].value);
    focusManualOutline();
  };

  const handleReturnToChat = () => {
    setInteractionMode("chat");
    if (typeof window === "undefined") return;
    window.setTimeout(() => {
      chatInputRef.current?.focus();
    }, 120);
  };

  const updateFormForTemplateSwitch = (previousTemplate: PrdTemplate, nextTemplate: PrdTemplate) => {
    setForm((prevForm) => {
      let nextForm = prevForm;
      const maybeClone = () => {
        if (nextForm === prevForm) {
          nextForm = { ...prevForm };
        }
      };

      const applyDefault = (
        currentValue: string,
        previousDefault: string | undefined,
        nextDefault: string | undefined,
        assign: (value: string) => void,
      ) => {
        if (!nextDefault) {
          return;
        }
        const trimmedCurrent = currentValue.trim();
        const trimmedPreviousDefault = previousDefault?.trim();
        if (
          trimmedCurrent.length === 0 ||
          (trimmedPreviousDefault && trimmedCurrent === trimmedPreviousDefault)
        ) {
          maybeClone();
          assign(nextDefault);
        }
      };

      applyDefault(
        prevForm.techPreferences,
        previousTemplate.defaults?.techPreferences,
        nextTemplate.defaults?.techPreferences,
        (value) => {
          nextForm.techPreferences = value;
        },
      );

      applyDefault(
        prevForm.backendServices,
        previousTemplate.defaults?.backendServices,
        nextTemplate.defaults?.backendServices,
        (value) => {
          nextForm.backendServices = value;
        },
      );

      return nextForm;
    });
  };

  const handleTemplateChange = (nextId: PrdTemplateId) => {
    if (templateId === nextId) {
      return;
    }
    trackBaseEvent("prd_template_change", { from: templateId, to: nextId });
    const nextTemplate = prdTemplateMap[nextId] ?? prdTemplateMap[defaultPrdTemplateId];
    const previousTemplate = activeTemplate;

    updateFormForTemplateSwitch(previousTemplate, nextTemplate);
    setTemplateId(nextId);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(TEMPLATE_STORAGE_KEY) as PrdTemplateId | null;
    if (stored && stored in prdTemplateMap) {
      if (stored === defaultPrdTemplateId) {
        return;
      }
      const storedTemplate = prdTemplateMap[stored as PrdTemplateId];
      updateFormForTemplateSwitch(prdTemplateMap[defaultPrdTemplateId], storedTemplate);
      setTemplateId(stored);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(TEMPLATE_STORAGE_KEY, templateId);
  }, [templateId]);

  // Restore draft from localStorage on mount
  useEffect(() => {
    if (draftRestoredRef.current) return;
    const draft = loadDraft();
    if (draft) {
      // Validate and restore form data
      if (draft.form && typeof draft.form === "object") {
        setForm((prev) => ({ ...prev, ...draft.form }));
      }
      // Restore chat messages if they have content beyond initial
      if (Array.isArray(draft.chatMessages) && draft.chatMessages.length > 1) {
        setChatMessages(draft.chatMessages);
      }
      // Restore markdown if present
      if (draft.markdown && typeof draft.markdown === "string") {
        setMarkdown(draft.markdown);
      }
      // Restore interaction mode
      if (draft.interactionMode === "chat" || draft.interactionMode === "manual") {
        setInteractionMode(draft.interactionMode);
      }
      // Restore template if valid
      if (draft.templateId && draft.templateId in prdTemplateMap) {
        setTemplateId(draft.templateId);
      }
      draftRestoredRef.current = true;
      console.log("[PRD] Draft restored from localStorage");
    }
  }, []);

  // Auto-save draft on changes (debounced 1 second)
  useEffect(() => {
    // Skip saving during initial restoration
    if (!draftRestoredRef.current) {
      // Mark as restored after first render to enable saving
      draftRestoredRef.current = true;
      return;
    }

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      window.clearTimeout(saveTimeoutRef.current);
    }

    // Debounce save by 1 second
    saveTimeoutRef.current = window.setTimeout(() => {
      saveDraft({
        form,
        chatMessages,
        markdown,
        templateId,
        interactionMode,
      });
    }, 1000);

    return () => {
      if (saveTimeoutRef.current) {
        window.clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [form, chatMessages, markdown, templateId, interactionMode]);

  const plan = useMemo(() => {
    const publicPlan =
      user?.publicMetadata?.plan ??
      user?.publicMetadata?.tier ??
      (user?.publicMetadata as Record<string, unknown> | undefined)?.pricingTier ??
      user?.unsafeMetadata?.plan;
    return normalisePlan(publicPlan);
  }, [user?.publicMetadata, user?.unsafeMetadata]);

  const trackBaseEvent = useCallback(
    (event: string, properties?: Record<string, unknown>) => {
      trackEvent(event, {
        page: "/prd-generator",
        session_id: sessionId,
        template_id: templateId,
        plan,
        signed_in: isSignedIn,
        ...properties,
      });
    },
    [sessionId, templateId, plan, isSignedIn],
  );

  const markActive = useCallback(
    (interaction?: string) => {
      if (hasMarkedActiveRef.current) return;
      hasMarkedActiveRef.current = true;
      trackBaseEvent("prd_active", {
        interaction: interaction ?? interactionMode,
      });
    },
    [interactionMode, trackBaseEvent],
  );

  const trackCtaClick = useCallback(
    (placement: string) => {
      trackBaseEvent("prd_cta_click", { placement });
    },
    [trackBaseEvent],
  );

  const monthKey = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  }, []);

  useEffect(() => {
    ensureAnalytics();
  }, []);

  useEffect(() => {
    registerContext({
      page: "/prd-generator",
      session_id: sessionId,
      template_id: templateId,
      plan,
      signed_in: isSignedIn,
    });
    if (hasTrackedPageViewRef.current) return;
    hasTrackedPageViewRef.current = true;
    trackBaseEvent("prd_page_view", {
      referrer: typeof document !== "undefined" ? document.referrer : null,
    });
  }, [sessionId, templateId, plan, isSignedIn, trackBaseEvent]);

  const usageStorageKey = useMemo(() => {
    if (!user?.id) return null;
    return `coderide-prd-usage-${user.id}-${monthKey}`;
  }, [monthKey, user?.id]);

  useEffect(() => {
    if (!usageStorageKey) return;
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(usageStorageKey);
    if (!stored) return;
    const parsed = Number(stored);
    if (Number.isFinite(parsed)) {
      setUsageCount(parsed);
    }
  }, [usageStorageKey]);

  useEffect(() => {
    if (!isGenerating) {
      setLoadingStepIndex(0);
      setGenerationProgress(0);
      setElapsedTime(0);
      if (elapsedTimeRef.current) {
        window.clearInterval(elapsedTimeRef.current);
        elapsedTimeRef.current = null;
      }
      return;
    }

    // Track elapsed time every second
    const startTime = Date.now();
    elapsedTimeRef.current = window.setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => {
      if (elapsedTimeRef.current) {
        window.clearInterval(elapsedTimeRef.current);
        elapsedTimeRef.current = null;
      }
    };
  }, [isGenerating]);

  // Lock body scroll during generation for full-screen modal
  useEffect(() => {
    if (isGenerating) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isGenerating]);

  useEffect(() => {
    if (!chatListRef.current) return;
    chatListRef.current.scrollTo({
      top: chatListRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [chatMessages]);

  useEffect(() => {
    return () => {
      if (outlineHighlightTimeoutRef.current) {
        window.clearTimeout(outlineHighlightTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!recentlyUpdatedKeys.length) return;
    const timer = window.setTimeout(() => {
      setRecentlyUpdatedKeys([]);
    }, 6000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [recentlyUpdatedKeys]);

  useEffect(() => {
    if (!chatInputRef.current) return;
    const element = chatInputRef.current;
    element.style.height = "auto";
    const contentHeight = Math.min(280, Math.max(60, element.scrollHeight));
    element.style.height = `${contentHeight}px`;
  }, [chatInput]);

  const baseQuota = isSignedIn
    ? (isExplorerPlan(plan) ? MONTHLY_EXPLORER_LIMIT : Infinity)
    : MONTHLY_EXPLORER_LIMIT;
  const quotaLimit = baseQuota === Infinity ? Infinity : baseQuota + shareBonusCredits;
  const remaining = quotaLimit === Infinity ? Infinity : Math.max(quotaLimit - usageCount, 0);
  const bonusText = shareBonusCredits > 0 ? ` (+${shareBonusCredits} bonus)` : "";
  const usageLabel = isSignedIn
    ? quotaLimit === Infinity
      ? "Unlimited PRD generations included in your plan."
      : `${remaining} of ${MONTHLY_EXPLORER_LIMIT}${bonusText} PRDs left for the current month.`
    : `${MONTHLY_EXPLORER_LIMIT} PRDs per month included in the free Explorer plan.`;
  const activeIndex = FORM_GROUPS.findIndex((group) => group.value === activeTab);
  const safeActiveIndex = activeIndex === -1 ? 0 : activeIndex;
  const activeGroup = FORM_GROUPS[safeActiveIndex];
  const nextGroup = FORM_GROUPS[(safeActiveIndex + 1) % FORM_GROUPS.length];
  const productNameCount = form.productName.length;
  const trimmedChatInput = chatInput.trim();
  const isChatInputValid = trimmedChatInput.length >= MIN_CHAT_CHARACTERS;
  const isChatMode = interactionMode === "chat";

  const handleUpdateField = (key: GuidedFieldKey | "productName", value: string) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
    markActive("manual_edit");
  };

  const recordGeneration = () => {
    if (!isSignedIn || !usageStorageKey || quotaLimit === Infinity || typeof window === "undefined") {
      return;
    }
    const next = usageCount + 1;
    setUsageCount(next);
    window.localStorage.setItem(usageStorageKey, String(next));
  };

  const handleGenerate = async () => {
    if (isGenerating) {
      return;
    }

    markActive("generate");

    if (!isSignedIn) {
      trackBaseEvent("prd_generate_blocked", { reason: "not_signed_in" });
      launchSignIn();
      return;
    }

    if (quotaLimit !== Infinity && usageCount >= quotaLimit) {
      trackBaseEvent("prd_generate_blocked", {
        reason: "quota_reached",
        usage_count: usageCount,
        quota_limit: quotaLimit,
      });
      toast({
        title: "Generation limit reached",
        description: "Upgrade your CodeRide plan to unlock unlimited PRD exports.",
      });
      return;
    }

    const fallbackMarkdown = buildMarkdown(form, user?.primaryEmailAddress?.emailAddress ?? null);

    setIsGenerating(true);
    setGenerationProgress(5);
    setLoadingStepIndex(0);

    try {
      trackBaseEvent("prd_generate_request", {
        template_id: templateId,
        outline_version: outlineVersion,
        usage_count: usageCount,
        quota_limit: quotaLimit,
      });

      const response = await fetch("/api/prd-generate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "text/event-stream",
        },
        body: JSON.stringify({
          ...form,
          templateId,
          sessionId,
          userId: user?.id ?? null,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to generate PRD with AI.");
      }

      // Check if we got SSE response
      const contentType = response.headers.get("Content-Type") || "";
      if (contentType.includes("text/event-stream") && response.body) {
        // Handle SSE streaming
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let finalMarkdown = "";
        let finalUsage: { totalTokens?: number } | undefined;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const jsonStr = line.slice(6);
            try {
              const event = JSON.parse(jsonStr) as {
                type: "progress" | "complete" | "error";
                progress?: number;
                step?: string;
                markdown?: string;
                usage?: { totalTokens?: number };
                error?: string;
              };

              if (event.type === "progress") {
                // Update progress from real server event
                setGenerationProgress(event.progress ?? 10);
                // Map progress to step index
                const progress = event.progress ?? 0;
                if (progress < 25) setLoadingStepIndex(0);
                else if (progress < 50) setLoadingStepIndex(1);
                else if (progress < 70) setLoadingStepIndex(2);
                else if (progress < 90) setLoadingStepIndex(3);
                else setLoadingStepIndex(4);
              } else if (event.type === "complete") {
                finalMarkdown = event.markdown || "";
                finalUsage = event.usage;
                setGenerationProgress(100);
                setLoadingStepIndex(4);
              } else if (event.type === "error") {
                throw new Error(event.error || "Generation failed");
              }
            } catch (parseError) {
              console.warn("[PRD] Failed to parse SSE event:", parseError);
            }
          }
        }

        const aiMarkdown = finalMarkdown.trim();
        const resultMarkdown = aiMarkdown || fallbackMarkdown;

        setMarkdown(resultMarkdown);
        setLastGeneratedAt(new Date());
        recordGeneration();
        toast({
          title: aiMarkdown ? "PRD draft generated" : "Generated draft with fallback",
          description: aiMarkdown
            ? "Copy or download the Markdown document to start collaborating."
            : "AI response was empty, so we filled in a structured draft from your answers.",
        });
        trackBaseEvent("prd_generate_success", {
          template_id: templateId,
          outline_version: outlineVersion,
          had_ai_markdown: Boolean(aiMarkdown),
          usage_count: usageCount + 1,
          quota_limit: quotaLimit,
          token_usage: finalUsage?.totalTokens ?? undefined,
        });
      } else {
        // Fallback to JSON response (non-SSE)
        let result: { markdown?: string; error?: string; usage?: { totalTokens?: number } };
        try {
          result = await response.json();
        } catch {
          result = {};
        }

        const aiMarkdown = (result.markdown || "").trim();
        const resultMarkdown = aiMarkdown || fallbackMarkdown;

        setMarkdown(resultMarkdown);
        setLastGeneratedAt(new Date());
        recordGeneration();
        toast({
          title: aiMarkdown ? "PRD draft generated" : "Generated draft with fallback",
          description: aiMarkdown
            ? "Copy or download the Markdown document to start collaborating."
            : "AI response was empty, so we filled in a structured draft from your answers.",
        });
        trackBaseEvent("prd_generate_success", {
          template_id: templateId,
          outline_version: outlineVersion,
          had_ai_markdown: Boolean(aiMarkdown),
          usage_count: usageCount + 1,
          quota_limit: quotaLimit,
          token_usage: result.usage?.totalTokens ?? undefined,
        });
      }
    } catch (error) {
      console.error("[PRD] generation failed", error);
      setMarkdown(fallbackMarkdown);
      setLastGeneratedAt(new Date());
      recordGeneration();
      toast({
        title: "AI temporarily unavailable",
        description: "Generated a structured draft from your inputs instead. Try again later for an enriched version.",
      });
      trackBaseEvent("prd_generate_error", {
        template_id: templateId,
        outline_version: outlineVersion,
        error: error instanceof Error ? error.message : "unknown",
      });
    } finally {
      // Show 100% completion briefly before closing the modal
      setGenerationProgress(100);
      setLoadingStepIndex(4);
      await new Promise((resolve) => setTimeout(resolve, 400));
      setIsGenerating(false);
      // Scroll to the markdown section after modal closes
      setTimeout(() => {
        markdownSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  };

  const handleCopy = async () => {
    if (!markdown) {
      toast({
        title: "Nothing to copy yet",
        description: "Fill out the guided prompts and generate your PRD first.",
      });
      return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(markdown);
        toast({
          description: "Markdown PRD copied to your clipboard.",
        });
        trackBaseEvent("prd_copy_markdown");
      } else {
        throw new Error("Clipboard API not available");
      }
    } catch {
      toast({
        title: "Copy failed",
        description: "Your browser blocked clipboard access. Try downloading the file instead.",
      });
    }
  };

  const handleDownload = () => {
    if (!markdown) {
      toast({
        title: "Nothing to download yet",
        description: "Generate the PRD before exporting Markdown.",
      });
      return;
    }

    if (typeof window === "undefined") return;

    const filename = `${slugify(form.productName || "coderide-prd") || "coderide-prd"}.md`;
    const blob = new Blob([markdown], { type: "text/markdown" });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast({
      description: `Downloading ${filename}`,
    });
    trackBaseEvent("prd_download_markdown", { filename });
  };

  const handleShare = (platform: SharePlatform) => {
    const shareText = `I just created a PRD with CodeRide's AI-powered generator! Check it out:`;
    const shareUrl = "https://coderide.ai/prd-generator";

    const urls: Record<SharePlatform, string> = {
      x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      bluesky: `https://bsky.app/intent/compose?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
      email: `mailto:?subject=${encodeURIComponent("Check out CodeRide's PRD Generator")}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`,
    };

    // Open share dialog
    if (platform === "email") {
      window.location.href = urls[platform];
    } else {
      window.open(urls[platform], "_blank", "width=600,height=400,noopener,noreferrer");
    }

    // Try to claim credit
    const credited = claimShareCredit(platform);
    if (credited) {
      setShareBonusCredits((prev) => prev + 1);
      toast({
        title: "Thanks for sharing!",
        description: "+1 bonus PRD credit added to your account this month.",
      });
    }

    trackBaseEvent("prd_share_click", { platform, credited });
  };

  const handleAttachmentChange = async (file: File | null) => {
    setAttachmentError(null);

    if (!file) {
      setAttachmentName(null);
      setAttachmentPreview(null);
      setAttachmentSource(null);
      setAttachmentFormat(null);
      setAttachmentTruncated(false);
      setIsAttachmentUploading(false);
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = "";
      }
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
    const extensionAllowed =
      extension.length === 0 || SUPPORTED_ATTACHMENT_EXTENSIONS.includes(extension);
    const mimeAllowed =
      !file.type || file.type.startsWith("text/") || file.type.includes("pdf") || file.type.includes("word");

    if (!extensionAllowed && !mimeAllowed) {
      toast({
        title: "Unsupported attachment",
        description: "Upload Markdown, text, PDF, or Office-style documents.",
        variant: "destructive",
      });
      return;
    }

    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      toast({
        title: "Attachment too large",
        description: "Keep supporting docs under 8MB so we can process them quickly.",
        variant: "destructive",
      });
      return;
    }

    setAttachmentName(file.name);
    setAttachmentPreview(null);
    setAttachmentSource(null);
    setAttachmentFormat(null);
    setAttachmentTruncated(false);
    setIsAttachmentUploading(true);

    const formData = new FormData();
    formData.append("file", file, file.name);

    try {
      const response = await fetch("/api/upload-doc", {
        method: "POST",
        body: formData,
      });

      const isJson = response.headers.get("Content-Type")?.includes("application/json");
      const rawData = isJson ? await response.json() : await response.text();
      const parsed =
        typeof rawData === "string"
          ? { error: rawData }
          : {
              content: rawData?.content,
              format: rawData?.format,
              truncated: rawData?.truncated,
              source: rawData?.source,
              error: rawData?.error,
              warning: rawData?.warning,
            };

      if (!response.ok || !parsed.content) {
        throw new Error(
          parsed.error || "We couldn't process that file. Try uploading a different document.",
        );
      }

      setAttachmentPreview(parsed.content);
      setAttachmentSource(parsed.source === "llama-parse" ? "llama-parse" : "inline-text");
      setAttachmentFormat(parsed.format ?? null);
      setAttachmentTruncated(Boolean(parsed.truncated));
      toast({
        description: `Attached ${file.name}`,
      });
      markActive("attachment");
      trackBaseEvent("prd_attachment_upload", {
        result: "success",
        source: parsed.source ?? "inline-text",
        truncated: Boolean(parsed.truncated),
        format: parsed.format ?? null,
        size_kb: Math.round(file.size / 1024),
        extension,
      });
      if (parsed.warning) {
        toast({
          title: "Used basic text import",
          description: parsed.warning,
        });
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "We couldn't process that file. Try uploading a different document.";
      setAttachmentError(message);
      setAttachmentName(null);
      setAttachmentPreview(null);
      setAttachmentSource(null);
      setAttachmentFormat(null);
      setAttachmentTruncated(false);
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = "";
      }
      toast({
        title: "Attachment error",
        description: message,
        variant: "destructive",
      });
      trackBaseEvent("prd_attachment_upload", {
        result: "error",
        error: message,
        size_kb: file?.size ? Math.round(file.size / 1024) : undefined,
        extension,
      });
    } finally {
      setIsAttachmentUploading(false);
    }
  };

  const handleChatSubmit = async () => {
    if (isChatLoading || !isChatInputValid || isAttachmentUploading) return;
    const prompt = trimmedChatInput;
    markActive("chat");
    trackBaseEvent("prd_chat_submit", {
      mode: outlineVersion ? "adjust" : "bootstrap",
      outline_version: outlineVersion,
      attachment_present: Boolean(attachmentPreview && attachmentName),
      prompt_length: prompt.length,
      template_id: templateId,
    });

    setChatMessages((prev) => [...prev, { role: "user", content: prompt }]);
    setChatInput("");
    setIsChatLoading(true);

    let hasOutlineContext = false;
    try {
      const payload: Record<string, unknown> = {
        prompt,
        templateId,
        sessionId,
        userId: user?.id ?? null,
      };

      hasOutlineContext =
        outlineVersion !== null ||
        Object.values(form).some((value) => (value ?? "").trim().length > 0);

      if (hasOutlineContext) {
        payload.currentOutline = form;
        payload.mode = outlineVersion ? "adjust" : "bootstrap";
        if (outlineVersion) {
          payload.outlineVersion = outlineVersion;
        }
      }
      if (attachmentPreview && attachmentName) {
        const attachmentPayload: Record<string, unknown> = {
          name: attachmentName,
          content: attachmentPreview.slice(0, MAX_ATTACHMENT_CHARS),
        };
        if (attachmentSource) {
          attachmentPayload.source = attachmentSource;
        }
        if (attachmentFormat) {
          attachmentPayload.format = attachmentFormat;
        }
        if (attachmentTruncated) {
          attachmentPayload.truncated = true;
        }
        payload.attachment = attachmentPayload;
      }

      const response = await fetch("/api/prd-bootstrap", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      let data: BootstrapResponse;
      try {
        const text = await response.text();
        if (!text || text.trim().length === 0) {
          throw new Error("Server returned an empty response. The AI service may be temporarily unavailable.");
        }
        data = JSON.parse(text) as BootstrapResponse;
      } catch (parseError) {
        console.error("[Bootstrap] Response parse error:", parseError);
        throw new Error(
          parseError instanceof SyntaxError
            ? "Server returned an invalid response. Please try again."
            : parseError instanceof Error
              ? parseError.message
              : "Failed to process server response.",
        );
      }
      if (!response.ok) {
        if (response.status === 409) {
          if (data.outlineVersion) {
            setOutlineVersion(data.outlineVersion);
          }
          if (data.fields && typeof data.fields === "object") {
            const sanitizedConflictFields = sanitizeBootstrapFields(data.fields);
            setForm((prev) => ({
              ...prev,
              ...sanitizedConflictFields,
            }));
          }
          if (Array.isArray(data.history)) {
            setOutlineHistory(data.history);
          }
        }
        throw new Error(
          data?.error || "Failed to draft the outline. Try again or switch to manual mode.",
        );
      }

      const sanitizedFields = sanitizeBootstrapFields(data.updatedFields ?? data.fields);
      const updatedFieldKeys = (data.updatedKeys ?? Object.keys(sanitizedFields)) as string[];
      let outlineCalloutMessage: ChatMessage | null = null;

      if (updatedFieldKeys.length > 0) {
        setForm((prev) => ({
          ...prev,
          ...sanitizedFields,
        }));
        setActiveTab(FORM_GROUPS[0].value);
        setMarkdown("");
        setLastGeneratedAt(null);
        setAttachmentName(null);
        setAttachmentPreview(null);
        setAttachmentSource(null);
        setAttachmentFormat(null);
        setAttachmentTruncated(false);
        setAttachmentError(null);
        if (attachmentInputRef.current) {
          attachmentInputRef.current.value = "";
        }
        toast({
          description: "Drafted the outline. Review or edit the fields before generating your PRD.",
        });

        const updatedFieldLabels = updatedFieldKeys
          .map((key) => resolveFormFieldLabel(key))
          .filter((label): label is string => Boolean(label));
        const highlightedLabels = updatedFieldLabels.slice(0, 3);
        if (updatedFieldLabels.length > 3) {
          highlightedLabels.push(`+${updatedFieldLabels.length - 3} additional sections`);
        }

        outlineCalloutMessage = {
          role: "assistant",
          content: "",
          variant: "outline-ready",
          meta: {
            title: "Outline drafted",
            body:
              updatedFieldLabels.length > 0
                ? `We pre-filled ${updatedFieldLabels.length} section${updatedFieldLabels.length === 1 ? "" : "s"} from your brief. Review the outline to tweak details before generating the final PRD.`
                : "Your guided outline is ready. Review the sections before generating the final PRD.",
            bulletPoints: highlightedLabels,
            buttonLabel: "Review outline",
            secondaryLabel: "Keep chatting",
          },
        };
      }

      const highlightableKeys = updatedFieldKeys.filter(
        (key): key is GuidedFieldKey | "productName" => key === "productName" || key in FIELD_LOOKUP,
      );
      if (highlightableKeys.length > 0) {
        setRecentlyUpdatedKeys(highlightableKeys);
      }

      if (data.outlineVersion) {
        setOutlineVersion(data.outlineVersion);
      } else if (!outlineVersion) {
        const fallbackVersion =
          typeof window !== "undefined" && window.crypto?.randomUUID
            ? window.crypto.randomUUID()
            : `${Date.now()}`;
        setOutlineVersion(fallbackVersion);
      }

      if (Array.isArray(data.history)) {
        setOutlineHistory(data.history);
      }

      const assistantContent =
        data.summary?.trim() ||
        "I've drafted the guided outline. Review the sections in the manual outline before generating your PRD.";

      trackBaseEvent("prd_chat_success", {
        mode: data.mode ?? (hasOutlineContext ? "adjust" : "bootstrap"),
        outline_version: data.outlineVersion ?? outlineVersion,
        updated_fields: updatedFieldKeys.length,
        has_attachment: Boolean(attachmentPreview && attachmentName),
        template_id: templateId,
        token_usage: data.usage?.totalTokens ?? undefined,
      });

      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: assistantContent,
        },
        ...(outlineCalloutMessage ? [outlineCalloutMessage] : []),
      ]);
    } catch (error) {
      console.error("[PRD] bootstrap failed", error);

      trackBaseEvent("prd_chat_error", {
        mode: hasOutlineContext ? "adjust" : "bootstrap",
        outline_version: outlineVersion,
        error: error instanceof Error ? error.message : "unknown",
      });

      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "I couldn't turn that into a draft just yet. You can adjust your description or switch to the manual outline.",
        },
      ]);

      toast({
        title: "Could not draft outline",
        description:
          error instanceof Error
            ? error.message
            : "The assistant hit a snag. Try again in a moment or use the manual outline.",
        variant: "destructive",
      });
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleReset = () => {
    if (sessionId) {
      void fetch("/api/prd-reset", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ sessionId }),
      }).catch((error) => {
        console.error("[PRD] reset KV failed", error);
      });
    }
    // Clear persisted draft from localStorage
    clearDraft();
    setForm(DEFAULT_FORM);
    trackBaseEvent("prd_usage_reset");
    setMarkdown("");
    setLastGeneratedAt(null);
    setOutlineVersion(null);
    setOutlineHistory([]);
    setRecentlyUpdatedKeys([]);
    setInteractionMode("chat");
    setChatMessages(INITIAL_CHAT_MESSAGES);
    setChatInput("");
    setIsChatLoading(false);
    setAttachmentName(null);
    setAttachmentPreview(null);
    setAttachmentSource(null);
    setAttachmentFormat(null);
    setAttachmentTruncated(false);
    setAttachmentError(null);
    setIsAttachmentUploading(false);
    setActiveTab(FORM_GROUPS[0].value);
  };

  const redirectUrl = typeof window !== "undefined" ? window.location.href : "/prd-generator";

  const launchSignUp = async () => {
    try {
      await openSignUp({
        redirectUrl,
        afterSignUpUrl: "/prd-generator",
      });
    } catch {
      if (typeof window !== "undefined") {
        const fallback = `https://app.coderide.ai/auth/sign-up?redirect_url=${encodeURIComponent(redirectUrl)}`;
        window.open(fallback, "_blank", "noopener");
      }
    }
  };

  const launchSignIn = async () => {
    try {
      await openSignIn({
        redirectUrl,
        afterSignInUrl: "/prd-generator",
      });
    } catch {
      if (typeof window !== "undefined") {
        const fallback = `https://app.coderide.ai/auth/sign-in?redirect_url=${encodeURIComponent(redirectUrl)}`;
        window.open(fallback, "_blank", "noopener");
      }
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#050505] text-white">
      <SEO
        title="Free PRD Generator for AI Product Teams | CodeRide"
        description="Create polished product requirement documents in minutes. Guide your team through a structured PRD workflow optimised for CodeRide builds."
        canonical="https://coderide.ai/prd-generator"
        keywords={[
          "prd generator",
          "product requirements template",
          "free prd tool",
          "software specification generator",
          "ai product discovery",
          "coderide prd",
        ]}
        topics={["Product management", "AI development workflow", "CodeRide tools"]}
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "CodeRide PRD Generator",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web",
            "url": "https://coderide.ai/prd-generator",
            "description": "AI-assisted product requirements generator that produces Markdown-ready PRDs from guided briefs or chat prompts.",
            "publisher": {
              "@type": "Organization",
              "name": "CodeRide",
              "url": "https://coderide.ai/",
            },
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "category": "Freemium",
              "url": "https://coderide.ai/prd-generator",
              "availability": "https://schema.org/InStock",
            },
            "featureList": [
              "Guided outline with AI suggestions",
              "Chat-first briefing with LlamaParse uploads",
              "Markdown export for CodeRide workflows",
            ],
          },
          FAQ_SCHEMA,
        ]}
      />
      <Header />
      <main className="flex-1">
        <section
          className="border-b border-white/10 bg-gradient-to-b from-[#0a0a0f] to-transparent py-20"
          aria-labelledby="prd-generator-hero"
        >
          <div className="container">
            <div className="grid items-start gap-12 lg:grid-cols-[1.05fr,0.95fr]">
              <div className="max-w-2xl space-y-8">
                <div>
                  <Badge variant="outline" className="border-coderide-light-blue/40 text-coderide-light-blue">
                    New • Free Tool
                  </Badge>
                  <h1 id="prd-generator-hero" className="mt-6 text-4xl font-bold leading-tight sm:text-5xl">
                    Ship with clarity. Generate a PRD that keeps your CodeRide build on track.
                  </h1>
                  <p className="mt-4 text-lg text-white/70">
                    Answer a handful of guided prompts, then copy or download a Markdown PRD ready for your team and agents.
                    Explorer accounts can draft up to three PRDs each month for free.
                  </p>
                </div>

                <div className="flex flex-wrap gap-3">
                  {!isSignedIn ? (
                    <Button
                      variant="primary"
                      className="gap-2"
                      onClick={() => {
                        trackCtaClick("hero-start");
                        if (typeof window === "undefined") return;
                        const section = document.getElementById("prd-wizard");
                        if (section) {
                          section.scrollIntoView({ behavior: "smooth", block: "start" });
                        }
                      }}
                    >
                      <Sparkles className="h-4 w-4" />
                      Start for free
                    </Button>
                  ) : (
                    <div className="flex items-center gap-4 rounded-full border border-white/10 bg-white/5 px-5 py-3">
                      <img
                        src={user?.imageUrl}
                        alt={user?.fullName ?? user?.primaryEmailAddress?.emailAddress ?? "User avatar"}
                        className="h-10 w-10 rounded-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                      <div className="flex flex-col text-left">
                        <span className="text-base font-semibold text-white">
                          {user?.fullName || user?.username || user?.primaryEmailAddress?.emailAddress}
                        </span>
                        {user?.primaryEmailAddress?.emailAddress && (
                          <span className="text-sm text-white/70">{user.primaryEmailAddress.emailAddress}</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-5 space-y-2">
                  <p className="text-xs font-semibold text-white/60">Built for your AI stack</p>
                  <div className="flex flex-wrap items-center gap-2">
                    {AI_TOOL_LOGOS.map((tool) => (
                      <span key={tool.name} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5">
                        <img src={tool.logo} alt={tool.alt} className="h-8 w-8 rounded-full object-contain" loading="lazy" />
                      </span>
                    ))}
                  </div>
                </div>

              </div>

              <div className="relative mx-auto w-full max-w-xl">
                <div className="pointer-events-none absolute inset-0 rounded-[48px] bg-gradient-to-br from-coderide-light-blue/25 via-transparent to-white/5 blur-[80px]" />
                <div className="relative z-10 flex flex-col gap-6">
                  <div className="overflow-hidden rounded-[36px] border border-white/10 bg-[#0d0e19]/90 shadow-[0_40px_120px_-60px_rgba(8,8,16,0.9)]">
                    <div className="flex items-center gap-2 border-b border-white/10 bg-[#111225]/80 px-6 py-4">
                      <span className="h-3 w-3 rounded-full bg-red-500" />
                      <span className="h-3 w-3 rounded-full bg-yellow-500" />
                      <span className="h-3 w-3 rounded-full bg-green-500" />
                    </div>
                    <div className="p-6">
                      <pre className="whitespace-pre-wrap font-mono text-[13px] leading-relaxed text-white/80">
                        {HERO_PREVIEW_SNIPPET}
                      </pre>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-white/55">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-coderide-light-blue" />
                      Industry-standard PRD outline
                    </div>
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-coderide-light-blue" />
                      Optimised for CodeRide workflows
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="container py-16" aria-labelledby="guided-brief">
          <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="space-y-6" id="prd-wizard">
              <div className="relative">
                {isGenerating && activeLoadingStep && (
                  <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/95 px-6 py-8 text-center backdrop-blur-lg">
                    {/* CodeRide logo - top left */}
                    <div className="absolute left-6 top-6">
                      <img
                        src="https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/Logo%20White.svg?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL0xvZ28gV2hpdGUuc3ZnIiwiaWF0IjoxNzQ0OTIxODI5LCJleHAiOjE3NzY0NTc4Mjl9.Zk_7kzu1sXIdqhsO2IpRSy3sAOhBKkB9XKnhMq5t7Co"
                        alt="CodeRide"
                        className="h-5 w-auto opacity-70"
                        draggable={false}
                      />
                    </div>

                    {/* Main content container */}
                    <div className="flex w-full max-w-md flex-col items-center gap-8">

                      {/* Animated icon with glow - slower animations */}
                      <div className="relative flex h-24 w-24 items-center justify-center">
                        <div className="absolute h-full w-full animate-ping rounded-full bg-coderide-light-blue/10" style={{ animationDuration: '3s' }} />
                        <div className="absolute h-full w-full animate-pulse rounded-full bg-coderide-light-blue/20 blur-2xl" style={{ animationDuration: '3s' }} />
                        <div className="absolute h-full w-full animate-[spin_12s_linear_infinite] rounded-full border-2 border-transparent border-t-coderide-light-blue/50" />
                        <div className="relative flex h-16 w-16 items-center justify-center rounded-full border-2 border-coderide-light-blue/60 bg-gradient-to-br from-coderide-light-blue/20 to-transparent">
                          <FileText className="h-7 w-7 text-coderide-light-blue" />
                        </div>
                      </div>

                      {/* Title and phase indicator */}
                      <div className="flex flex-col items-center gap-3">
                        <p className="text-xl font-semibold text-white">{activeLoadingStep.title}</p>
                        <div className="flex items-center gap-2">
                          <span className="animate-pulse rounded-full bg-coderide-light-blue/20 px-3 py-1 text-xs font-medium text-coderide-light-blue" style={{ animationDuration: '2.5s' }}>
                            {loadingStepIndex < 3 ? "Phase 1 of 2" : "Phase 2 of 2"}
                          </span>
                          <span className="text-xs text-white/40">•</span>
                          <span className="text-xs tabular-nums text-white/50">
                            {elapsedTime}s
                          </span>
                        </div>
                      </div>

                      {/* Step indicators */}
                      <div className="flex items-center gap-2">
                        {GENERATION_STEPS.map((step, index) => (
                          <div
                            key={step.title}
                            className={cn(
                              "flex h-2.5 w-2.5 items-center justify-center rounded-full transition-all duration-300",
                              index < loadingStepIndex
                                ? "bg-coderide-light-blue"
                                : index === loadingStepIndex
                                  ? "bg-coderide-light-blue ring-4 ring-coderide-light-blue/20"
                                  : "bg-white/20"
                            )}
                            style={index === loadingStepIndex ? { animation: 'pulse 2.5s cubic-bezier(0.4, 0, 0.6, 1) infinite' } : undefined}
                          />
                        ))}
                      </div>

                      {/* Progress bar */}
                      <div className="w-full space-y-3">
                        <div className="relative h-2 w-full overflow-hidden rounded-full bg-white/10">
                          {/* Animated shimmer overlay - slower */}
                          <div
                            className="absolute inset-0 -translate-x-full animate-[shimmer_3s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent"
                            style={{ animationTimingFunction: 'ease-in-out' }}
                          />
                          {/* Actual progress */}
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-coderide-light-blue via-coderide-light-blue/90 to-coderide-light-blue/70 transition-all duration-500 ease-out"
                            style={{ width: `${generationProgress}%` }}
                          />
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-white/40">{Math.round(generationProgress)}%</span>
                          <span className="text-white/40">
                            Step {loadingStepIndex + 1} of {GENERATION_STEPS.length}
                          </span>
                        </div>
                      </div>

                      {/* Description */}
                      <p className="max-w-sm text-sm leading-relaxed text-white/60">
                        {GENERATION_STEPS[loadingStepIndex]?.description ?? "Processing your requirements..."}
                      </p>

                      {/* Activity indicator */}
                      <div className="flex items-center gap-2 text-xs text-white/40">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        <span>AI is actively processing your PRD</span>
                      </div>
                    </div>
                  </div>
                )}
                <Card
                  aria-busy={isGenerating}
                  className={cn(
                    "border border-white/10 bg-white/5 backdrop-blur transition-opacity",
                    isGenerating ? "pointer-events-none opacity-50" : "opacity-100",
                  )}
                >
                <CardHeader className="pb-0">
                  <div className="grid gap-6 sm:grid-cols-2 sm:items-start">
                    <div className="space-y-2">
                      <CardTitle id="guided-brief" className="text-2xl font-semibold text-white">Guided brief</CardTitle>
                      <CardDescription className="text-white/70">
                        Start with a chat to draft the outline or switch to manual entry—every field stays editable. Use line breaks to add bullet points before generating the PRD.
                      </CardDescription>
                    </div>
                    <div className="flex flex-col gap-2 text-xs sm:items-end sm:text-right">
                      <div className="flex w-full flex-wrap items-center gap-2 sm:justify-end">
                        <span className="uppercase tracking-wide text-white/40">Current plan</span>
                        <img
                          src={`/assets/tiers/${resolvePlanBadge(isSignedIn ? plan : "Explorer")}.svg`}
                          alt={`${isSignedIn ? plan : "Explorer"} badge`}
                          className="h-5 w-auto sm:h-6"
                          draggable={false}
                        />
                      </div>
                      <div className="flex w-full items-center gap-2 text-white/60 sm:justify-end">
                        <Info className="h-3.5 w-3.5 text-coderide-light-blue" />
                        <span>{usageLabel}</span>
                      </div>
                      <div className="flex w-full flex-wrap items-center gap-2 sm:justify-end">
                        {!isSignedIn ? (
                          <Button
                            variant="primary"
                            onClick={() => {
                              trackCtaClick("guided-start");
                              launchSignUp();
                            }}
                            size="sm"
                            className="gap-2"
                          >
                            Start for free
                          </Button>
                        ) : isExplorerPlan(plan) ? (
                          <Button
                            variant="secondary"
                            onClick={() => {
                              trackCtaClick("guided-upgrade");
                              if (typeof window !== "undefined") {
                                window.open("/pricing", "_blank", "noopener");
                              }
                            }}
                            size="sm"
                            className="gap-1"
                          >
                            Upgrade
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6 pt-6">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between gap-4">
                      <label htmlFor="productName" className="text-sm font-medium text-white">
                        Product name
                      </label>
                      <span className="text-xs text-white/45">
                        {productNameCount}/{PRODUCT_NAME_LIMIT}
                      </span>
                    </div>
                    {recentlyUpdatedKeys.includes("productName") && (
                      <span className="text-[11px] font-semibold text-coderide-light-blue">Updated via chat</span>
                    )}
                    <Input
                      id="productName"
                      maxLength={PRODUCT_NAME_LIMIT}
                      className={cn(
                        "rounded-lg border border-white/15 bg-black/25 px-4 py-3 text-sm text-white transition hover:border-white/25 focus-visible:border-coderide-light-blue focus-visible:ring-2 focus-visible:ring-coderide-light-blue focus-visible:ring-offset-0",
                        recentlyUpdatedKeys.includes("productName") &&
                          "border-coderide-light-blue/60 bg-coderide-light-blue/5 shadow-[0_0_20px_-8px_rgba(120,103,238,0.6)]",
                      )}
                      placeholder="Example: CodeRide PRD Generator"
                      value={form.productName}
                      onChange={(event) => handleUpdateField("productName", event.target.value)}
                      onBlur={() =>
                        trackBaseEvent("prd_outline_edit", {
                          field_key: "productName",
                          length: form.productName.trim().length,
                          mode: interactionMode,
                        })
                      }
                    />
                    <div className="flex items-start gap-3 rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-xs text-white/65">
                      <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-coderide-light-blue" />
                      <p>
                        Tip: Be specific and descriptive. A clear product name helps downstream agents generate sharper PRDs.
                      </p>
                    </div>
                  </div>

                <div className="rounded-2xl border border-white/10 bg-black/25 p-4" aria-labelledby="chat-manual-toggle">
                  {isChatMode ? (
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="space-y-1">
                          <p id="chat-manual-toggle" className="text-xs uppercase tracking-wide text-white/40">Chat Assistant</p>
                            <p className="text-sm font-medium text-white/80">
                              Describe your build and we’ll draft the outline for you.
                            </p>
                          </div>
                          <div className="flex items-center gap-2 rounded-full border border-white/15 bg-black/40 p-1 text-xs">
                            <button
                              type="button"
                              onClick={() => setInteractionMode("chat")}
                              className={cn(
                                "rounded-full px-3 py-1 transition",
                                isChatMode
                                  ? "bg-coderide-light-blue text-[#050505]"
                                  : "text-white/60 hover:text-white",
                              )}
                            >
                              Chat
                            </button>
                            <button
                              type="button"
                              onClick={() => setInteractionMode("manual")}
                              className={cn(
                                "rounded-full px-3 py-1 transition",
                                !isChatMode
                                  ? "bg-coderide-light-blue text-[#050505]"
                                  : "text-white/60 hover:text-white",
                              )}
                            >
                              Manual
                            </button>
                          </div>
                        </div>
                        <div
                          ref={chatListRef}
                          className="chat-scrollbar flex min-h-[4.5rem] max-h-64 flex-col gap-3 overflow-y-auto pr-1 sm:max-h-80"
                        >
                            {chatMessages.map((message, index) => {
                              const isAssistant = message.role === "assistant";
                            const avatar = isAssistant ? (
                              <div className="flex h-8 w-8 shrink-0 overflow-hidden rounded-full border border-white/15 bg-black/40">
                                <img
                                  src="/assets/coderide_logo_avatar.png"
                                  alt="CodeRide"
                                  className="h-full w-full object-cover"
                                  draggable={false}
                                />
                              </div>
                            ) : (
                              <div className="h-8 w-8 shrink-0 overflow-hidden rounded-full border border-white/15 bg-white/10">
                                {user?.imageUrl ? (
                                  <img
                                    src={user.imageUrl}
                                    alt={user.fullName ?? user.username ?? "User avatar"}
                                    className="h-full w-full object-cover"
                                    referrerPolicy="no-referrer"
                                  />
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center bg-coderide-light-blue/20 text-xs text-white">
                                    {user?.firstName?.[0]?.toUpperCase() || "U"}
                                  </div>
                                )}
                              </div>
                            );

                            const isOutlineCallout = message.variant === "outline-ready" && isAssistant;
                            return (
                              <div
                                key={`${message.role}-${index}`}
                                className={cn("flex w-full", isAssistant ? "justify-start" : "justify-end")}
                              >
                                <div
                                  className={cn(
                                    "flex max-w-[80%] gap-3",
                                    isAssistant ? "flex-row" : "flex-row-reverse",
                                    isOutlineCallout ? "items-start" : "items-end",
                                  )}
                                >
                                  {avatar}
                                  {isOutlineCallout ? (
                                    <div className="flex w-full flex-col gap-3 rounded-2xl border border-coderide-light-blue/35 bg-coderide-light-blue/10 px-4 py-3 text-sm text-white/85 shadow-[0_18px_50px_-30px_rgba(120,103,238,0.9)] backdrop-blur">
                                      <div className="flex items-center gap-2">
                                        <CheckCircle2 className="h-5 w-5 text-coderide-light-blue" />
                                        <span className="font-semibold text-white">
                                          {message.meta?.title ?? "Outline drafted"}
                                        </span>
                                      </div>
                                      {message.meta?.body && (
                                        <p className="text-xs text-white/70">
                                          {message.meta.body}
                                        </p>
                                      )}
                                      {message.meta?.bulletPoints?.length ? (
                                        <ul className="space-y-1 text-xs text-white/75">
                                          {message.meta.bulletPoints.map((point, pointIndex) => (
                                            <li key={`${point}-${pointIndex}`} className="flex items-start gap-2">
                                              <span className="mt-1 h-1.5 w-1.5 rounded-full bg-coderide-light-blue" />
                                              <span>{point}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      ) : null}
                                      <div className="flex flex-wrap items-center gap-2">
                                        <Button
                                          size="sm"
                                          variant="primary"
                                          onClick={handleOutlineReviewClick}
                                          showArrow
                                        >
                                          {message.meta?.buttonLabel ?? "Review outline"}
                                        </Button>
                                        {message.meta?.secondaryLabel && (
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={handleReturnToChat}
                                            className="text-white/75 hover:text-white"
                                          >
                                            {message.meta.secondaryLabel}
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <div
                                      className={cn(
                                        "rounded-2xl px-4 py-2 text-sm leading-relaxed shadow-sm",
                                        isAssistant
                                          ? "bg-white/10 text-white/80"
                                          : "bg-coderide-light-blue text-[#050505]",
                                      )}
                                    >
                                      {message.content}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          {isChatLoading && (
                            <div className="flex items-center gap-2 self-start rounded-2xl bg-white/10 px-4 py-2 text-xs text-white/70">
                              <Loader2 className="h-3.5 w-3.5 animate-spin text-coderide-light-blue" />
                              Drafting your outline…
                            </div>
                          )}
                        </div>
                        <div className="mt-8 flex flex-col gap-3">
                          <label className="text-xs text-white/55">
                            We’ll populate the outline using this description. Press Shift + Enter for a new line.
                          </label>
          <div
            className={cn(
              "relative flex flex-col overflow-hidden rounded-xl border border-dashed border-white/15 bg-black/25 p-3 transition hover:border-white/25",
              attachmentPreview && "border-solid",
            )}
            onDragOver={(event) => {
              if (isChatLoading || isAttachmentUploading) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = "copy";
            }}
            onDrop={(event) => {
              if (isChatLoading || isAttachmentUploading) return;
              event.preventDefault();
              const file = event.dataTransfer.files?.[0];
              if (file) {
                void handleAttachmentChange(file);
              }
            }}
          >
            <Textarea
              ref={chatInputRef}
              value={chatInput}
              onChange={(event) => setChatInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  handleChatSubmit();
                }
              }}
              placeholder="Describe the product, target users, and key capabilities…"
              className="chat-scrollbar !min-h-[60px] w-full rounded-xl border border-transparent bg-transparent px-3 py-1 pr-12 text-sm text-white transition focus-visible:border-coderide-light-blue focus-visible:ring-2 focus-visible:ring-coderide-light-blue focus-visible:ring-offset-0 overflow-y-auto resize-none sm:!min-h-[60px] !max-h-[280px]"
              disabled={isChatLoading}
            />
            <div className="mt-4 flex items-center justify-between gap-2 px-0">
              <Select
                value={templateId}
                onValueChange={(value) => handleTemplateChange(value as PrdTemplateId)}
              >
                <SelectTrigger className="flex !h-8 !w-auto items-center justify-start gap-1.5 rounded-full border-0 bg-transparent px-3 !py-0 text-[11px] font-medium text-white/70 hover:text-white focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 [&>span>svg]:text-white/40">
                  <ActiveTemplateIcon className="h-3.5 w-3.5 text-white/60" />
                  <span className="text-[11px] text-white/70">{activeTemplate.label}</span>
                </SelectTrigger>
                <SelectContent
                  align="start"
                  className="max-h-[320px] min-w-[280px] overflow-hidden rounded-xl border border-white/10 bg-[#101013] p-1 text-white shadow-xl"
                >
                  {prdTemplates.map((template) => {
                    const Icon = templateIconMap[template.icon];
                    return (
                      <SelectItem
                        key={template.id}
                        value={template.id}
                        className="flex items-start gap-3 rounded-lg px-3 py-2 text-left text-sm text-white data-[highlighted]:bg-white/10 data-[state=checked]:bg-[#7867EE] data-[state=checked]:text-white"
                      >
                        <div className="flex items-start gap-3">
                          <Icon className="mt-0.5 h-4 w-4 shrink-0" />
                          <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium text-white">{template.label}</span>
                            <span className="text-xs text-white/60">{template.description}</span>
                          </div>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-black/40 text-white/70 transition hover:border-white/25 hover:text-white disabled:opacity-40"
                  onClick={() => attachmentInputRef.current?.click()}
                  disabled={isChatLoading || isAttachmentUploading}
                >
                  <Paperclip className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  className={cn(
                    "inline-flex h-9 w-9 items-center justify-center rounded-full border border-coderide-light-blue bg-coderide-light-blue text-[#050505] transition hover:border-white hover:text-[#050505]",
                    (!isChatInputValid || isChatLoading || isAttachmentUploading) && "opacity-60",
                  )}
                  onClick={handleChatSubmit}
                  disabled={!isChatInputValid || isChatLoading || isAttachmentUploading}
                >
                  {isChatLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isAttachmentUploading ? (
                    <Loader2 className="h-4 w-4 animate-spin text-[#050505]" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
                          {(attachmentName || isAttachmentUploading) && (
                            <div className="rounded-lg border border-white/15 bg-black/30 px-3 py-2 text-xs text-white/70">
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex min-w-0 items-center gap-2">
                                  <FileText className="h-3.5 w-3.5 shrink-0 text-coderide-light-blue" />
                                  <div className="min-w-0">
                                    <p className="truncate text-sm font-medium text-white">
                                      {attachmentName || "Processing attachment…"}
                                    </p>
                                    {!isAttachmentUploading && attachmentSource === "llama-parse" && attachmentTruncated && (
                                      <p className="text-[11px] text-white/50">Trimmed to 4K chars</p>
                                    )}
                                    {!isAttachmentUploading && attachmentSource === "inline-text" && (
                                      <p className="text-[11px] text-white/50">Imported as plain text</p>
                                    )}
                                  </div>
                                </div>
                                {isAttachmentUploading ? (
                                  <Loader2 className="h-4 w-4 animate-spin text-coderide-light-blue" />
                                ) : (
                                  <button
                                    type="button"
                                    className="font-semibold text-coderide-light-blue hover:underline"
                                    onClick={() => {
                                      setAttachmentName(null);
                                      setAttachmentPreview(null);
                                      setAttachmentSource(null);
                                      setAttachmentFormat(null);
                                      setAttachmentTruncated(false);
                                      setAttachmentError(null);
                                      if (attachmentInputRef.current) {
                                        attachmentInputRef.current.value = "";
                                      }
                                    }}
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                          {attachmentError && (
                            <p className="text-xs font-medium text-red-400">
                              {attachmentError}
                            </p>
                          )}
                          <input
                            ref={attachmentInputRef}
                            type="file"
                            accept=".txt,.md,.markdown,.json,.pdf,.doc,.docx,.ppt,.pptx,.csv,.html,.htm,.rtf"
                            className="hidden"
                            onChange={(event) => void handleAttachmentChange(event.target.files?.[0] ?? null)}
                          />
                          <p className="text-xs text-white/40">
                            Focus: {activeTemplate.description}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div
                        ref={manualOutlineRef}
                        className={cn(
                          "space-y-4 rounded-2xl transition-shadow",
                          outlineHighlight
                            ? "ring-2 ring-coderide-light-blue/70 ring-offset-4 ring-offset-[#07090f]"
                            : "ring-0 ring-offset-0",
                        )}
                      >
                        {outlineHistory.length > 0 && (
                          <div className="rounded-2xl border border-coderide-light-blue/25 bg-[#0b0f16] px-5 py-4 text-sm text-white/85">
                            <div className="flex items-start gap-3">
                              <div className="flex h-9 w-9 items-center justify-center rounded-full border border-coderide-light-blue/40 bg-coderide-light-blue/10">
                                <Sparkles className="h-4 w-4 text-coderide-light-blue" />
                              </div>
                              <div className="flex-1 space-y-2">
                                <div className="text-[11px] font-semibold uppercase tracking-[0.3em] text-white/60">
                                  Latest chat update
                                </div>
                                <p className="text-sm text-white/90">
                                  {outlineHistory[0].summary || "Outline updated via chat."}
                                </p>
                                {outlineHistory[0].updatedKeys?.length ? (
                                  <div className="mt-2 flex flex-wrap gap-2">
                                    {outlineHistory[0].updatedKeys.map((key) => {
                                      const label = resolveFormFieldLabel(key) || key;
                                      return (
                                        <button
                                          type="button"
                                          key={`${outlineHistory[0].versionId}-${key}`}
                                          onClick={() => handleHistoryChipClick(key)}
                                          className="rounded-full border border-coderide-light-blue/35 bg-transparent px-3 py-1 text-[11px] font-medium text-white/80 transition hover:bg-coderide-light-blue/20 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coderide-light-blue focus-visible:ring-offset-0"
                                        >
                                          {label}
                                        </button>
                                      );
                                    })}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="flex flex-col gap-2">
                            <div className="space-y-1">
                              <p className="text-xs uppercase tracking-wide text-white/40">Manual outline</p>
                              <p className="text-sm font-medium text-white/80">
                                Step {safeActiveIndex + 1} of {FORM_GROUPS.length}
                              </p>
                            </div>
                            <Select
                              value={templateId}
                              onValueChange={(value) => handleTemplateChange(value as PrdTemplateId)}
                            >
                              <SelectTrigger className="pointer-events-auto flex h-auto w-auto items-center gap-1.5 rounded border-none bg-transparent px-0 py-0 text-[11px] font-medium text-white/60 hover:text-white focus:h-auto focus:w-auto focus:rounded focus:border-none focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 [&>span>svg]:text-white/40">
                                <ActiveTemplateIcon className="h-3.5 w-3.5 text-white/60" />
                                <span className="text-[11px] text-white/70">{activeTemplate.label}</span>
                              </SelectTrigger>
                              <SelectContent
                                align="start"
                                className="max-h-[320px] min-w-[280px] overflow-hidden rounded-xl border border-white/10 bg-[#101013] p-1 text-white shadow-xl"
                              >
                                {prdTemplates.map((template) => {
                                  const Icon = templateIconMap[template.icon];
                                  return (
                                    <SelectItem
                                      key={template.id}
                                      value={template.id}
                                      className="flex items-start gap-3 rounded-lg px-3 py-2 text-left text-sm text-white data-[highlighted]:bg-white/10 data-[state=checked]:bg-[#7867EE] data-[state=checked]:text-white"
                                    >
                                      <div className="flex items-start gap-3">
                                        <Icon className="mt-0.5 h-4 w-4 shrink-0" />
                                        <div className="flex flex-col gap-1">
                                          <span className="text-sm font-medium text-white">{template.label}</span>
                                          <span className="text-xs text-white/60">{template.description}</span>
                                        </div>
                                      </div>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-2 rounded-full border border-white/15 bg-black/40 p-1 text-xs">
                              <button
                                type="button"
                                onClick={() => setInteractionMode("chat")}
                                className={cn(
                                  "rounded-full px-3 py-1 transition",
                                  isChatMode
                                    ? "bg-coderide-light-blue text-[#050505]"
                                    : "text-white/60 hover:text-white",
                                )}
                              >
                                Chat
                              </button>
                              <button
                                type="button"
                                onClick={() => setInteractionMode("manual")}
                                className={cn(
                                  "rounded-full px-3 py-1 transition",
                                  !isChatMode
                                    ? "bg-coderide-light-blue text-[#050505]"
                                    : "text-white/60 hover:text-white",
                                )}
                              >
                                Manual
                              </button>
                            </div>
                            <button
                              type="button"
                              onClick={handleReset}
                              className="flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-black/40 text-white/70 transition hover:border-white/25 hover:text-white"
                            >
                              <RotateCcw className="h-4 w-4" />
                              <span className="sr-only">Reset outline</span>
                            </button>
                          </div>
                        </div>
                        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as typeof activeTab)}>
                          <TabsList className="mt-4 flex w-full gap-2">
                            {FORM_GROUPS.map((group) => (
                              <TabsTrigger
                                key={group.value}
                                value={group.value}
                                className="flex-1 py-2 data-[state=active]:bg-coderide-light-blue data-[state=active]:text-[#050505]"
                              >
                                <group.icon className="mx-auto h-4 w-4" />
                                <span className="sr-only">{group.label}</span>
                              </TabsTrigger>
                            ))}
                          </TabsList>
                          {FORM_GROUPS.map((group) => (
                            <TabsContent key={group.value} value={group.value} className="space-y-5">
                              <div className="rounded-xl border border-white/10 bg-black/30 p-4">
                                <p className="text-xs font-semibold uppercase tracking-wide text-coderide-light-blue">
                                  {group.kicker}
                                </p>
                                <p className="mt-1 text-sm font-semibold text-white">{group.label}</p>
                                <p className="mt-2 text-sm text-white/70">{group.description}</p>
                              </div>
                              <div className="space-y-4">
                                {group.fields.map((fieldKey) => {
                                  const field = FIELD_LOOKUP[fieldKey];

                                  if (field.key === "techPreferences") {
                                    return (
                                      <div key={field.key} className="space-y-3">
                                        <div className="flex flex-col gap-1">
                                          <span className="text-sm font-semibold text-white">{field.title}</span>
                                          <span className="text-xs text-white/55">{field.prompt}</span>
                                        </div>
                                        <div className="grid gap-2 sm:grid-cols-2">
                                          {TECH_OPTIONS.map((option) => {
                                            const selected = form.techPreferences === option.value;
                                            const isAIChoice = option.value === AI_DECIDE_LABEL;
                                            return (
                                              <button
                                                key={option.value}
                                                type="button"
                                                onClick={() => handleTechSelect(option.value)}
                                                className={cn(
                                                  "rounded-lg border border-white/15 bg-black/25 px-3 py-3 text-left transition hover:border-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coderide-light-blue",
                                                  selected && "border-coderide-light-blue bg-coderide-light-blue/10",
                                                )}
                                              >
                                                <div className="flex items-center gap-2">
                                                  {isAIChoice && (
                                                    <Sparkles
                                                      className={cn(
                                                        "h-4 w-4",
                                                        selected ? "text-coderide-light-blue" : "text-coderide-light-blue/70",
                                                      )}
                                                    />
                                                  )}
                                                  <span
                                                    className={cn(
                                                      "block text-sm font-semibold",
                                                      selected ? "text-white" : "text-white/80",
                                                    )}
                                                  >
                                                    {option.label}
                                                  </span>
                                                </div>
                                                <span
                                                  className={cn(
                                                    "mt-1 block text-xs",
                                                    selected ? "text-white/70" : "text-white/60",
                                                  )}
                                                >
                                                  {option.helper}
                                                </span>
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    );
                                  }

                                  if (field.key === "backendServices") {
                                    return (
                                      <div key={field.key} className="space-y-3">
                                        <div className="flex flex-col gap-1">
                                          <span className="text-sm font-semibold text-white">{field.title}</span>
                                          <span className="text-xs text-white/55">{field.prompt}</span>
                                        </div>
                                        <MultiSelect
                                          options={BACKEND_OPTIONS}
                                          value={backendSelected}
                                          onChange={handleBackendChange}
                                          placeholder="Select services (optional)"
                                        />
                                        {backendSelected.length > 0 && (
                                          <div className="flex flex-wrap gap-2">
                                            {backendSelected.map((service) => (
                                              <Badge
                                                key={service}
                                                variant="outline"
                                                className="border-white/20 text-white/80"
                                              >
                                                {service}
                                              </Badge>
                                            ))}
                                          </div>
                                        )}
                                        <p className="text-xs text-white/45">
                                          Leave empty to let the AI recommend backend services.
                                        </p>
                                      </div>
                                    );
                                  }

                                  return (
                                    <div key={field.key} className="space-y-2">
                                      <div className="flex flex-col gap-1">
                                        <span className="text-sm font-semibold text-white">{field.title}</span>
                                        <span className="text-xs text-white/55">{field.prompt}</span>
                                        {recentlyUpdatedKeys.includes(field.key) && (
                                          <span className="text-[11px] font-semibold text-coderide-light-blue">
                                            Updated via chat
                                          </span>
                                        )}
                                      </div>
                                      <div
                                        className={cn(
                                          "rounded-xl border border-dashed border-white/15 bg-black/20 p-3 transition hover:border-white/25",
                                          recentlyUpdatedKeys.includes(field.key) &&
                                            "border-coderide-light-blue/60 bg-coderide-light-blue/5 shadow-[0_0_20px_-8px_rgba(120,103,238,0.6)]",
                                        )}
                                      >
                                        <Textarea
                                          id={`field-${field.key}`}
                                          className="chat-scrollbar !min-h-[110px] w-full rounded-xl border border-transparent bg-transparent px-3 py-3 text-sm text-white shadow-none transition focus-visible:border-coderide-light-blue focus-visible:ring-2 focus-visible:ring-coderide-light-blue focus-visible:ring-offset-0 resize-none overflow-y-auto"
                                          value={form[field.key]}
                                          onChange={(event) => handleUpdateField(field.key, event.target.value)}
                                          onBlur={() =>
                                            trackBaseEvent("prd_outline_edit", {
                                              field_key: field.key,
                                              length: form[field.key].trim().length,
                                              mode: interactionMode,
                                            })
                                          }
                                          placeholder={field.placeholder}
                                        />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </TabsContent>
                          ))}
                        </Tabs>
                      </div>
                    )}
                  </div>

                  {!isChatMode && (
                    <div className="flex flex-wrap items-center gap-3">
                      <Button
                        variant="primary"
                        onClick={handleGenerate}
                        disabled={isGenerating}
                        className={cn(
                          "gap-2 disabled:cursor-not-allowed",
                          safeActiveIndex === FORM_GROUPS.length - 1 && "ml-auto"
                        )}
                      >
                        {isGenerating ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Generating…
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-4 w-4" />
                            {isSignedIn ? "Generate PRD" : "Log in to generate"}
                          </>
                        )}
                      </Button>
                      {safeActiveIndex < FORM_GROUPS.length - 1 && (
                        <div className="ml-auto w-full min-[420px]:w-auto">
                          <Button
                            variant="outline"
                            onClick={() => setActiveTab(nextGroup.value)}
                            type="button"
                            className="flex w-full items-center justify-center gap-2 min-[420px]:w-auto"
                          >
                            <ChevronRight className="h-4 w-4" />
                            Next section
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  {isSignedIn && quotaLimit !== Infinity && usageCount >= quotaLimit && (
                    <a
                      href="/pricing"
                      className="block text-sm font-medium text-coderide-light-blue underline-offset-4 hover:underline"
                    >
                      Upgrade for unlimited PRDs
                    </a>
                  )}
                  {lastGeneratedAt && (
                    <p className="text-xs text-white/50">
                      Last generated {lastGeneratedAt.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}.
                    </p>
                  )}
                  {!isSignedIn && (
                    <p className="text-xs text-white/50">
                      Don’t have an account yet? Choose “Sign up” inside the login modal to unlock the free Explorer tier.
                    </p>
                  )}
                </CardContent>
                </Card>
              </div>
            </div>

            <div className="space-y-6" ref={markdownSectionRef}>
              <Card className="border border-white/10 bg-white/5 backdrop-blur">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-xl text-white">
                    <Sparkles className="h-5 w-5 text-coderide-light-blue" />
                    Markdown PRD
                  </CardTitle>
                  <CardDescription className="text-white/70">
                    Copy or download the Markdown file and paste it into CodeRide, Notion, or your favourite docs tool.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-3">
                    <Button variant="outline" onClick={handleCopy} className="gap-2">
                      <ClipboardCopy className="h-4 w-4" />
                      Copy Markdown
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={handleDownload}
                      className="gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Download .md
                    </Button>
                    {markdown && (
                      <Button
                        variant="outline"
                        onClick={handleReset}
                        className="gap-2"
                      >
                        <RotateCcw className="h-4 w-4" />
                        Refresh
                      </Button>
                    )}
                  </div>
                  <ScrollArea className="h-[520px] rounded-md border border-white/10 bg-black/40 p-4">
                    <pre className="whitespace-pre-wrap text-sm leading-relaxed text-white/80">
                      {markdown || "Fill out the guided brief and generate your PRD to preview the Markdown output here."}
                    </pre>
                  </ScrollArea>
                  {markdown && (
                    <div className="flex flex-wrap items-center gap-3 border-t border-white/10 pt-4">
                      <span className="text-xs text-white/50">
                        <svg className="mr-1 inline h-3.5 w-3.5 text-coderide-light-blue" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        Share &amp; earn +1 credit:
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleShare("x")}
                        className="h-8 gap-1.5 px-2.5 text-white/70 hover:bg-white/10 hover:text-white"
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                        </svg>
                        <span className="hidden sm:inline">X</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleShare("linkedin")}
                        className="h-8 gap-1.5 px-2.5 text-white/70 hover:bg-white/10 hover:text-white"
                      >
                        <Linkedin className="h-4 w-4" />
                        <span className="hidden sm:inline">LinkedIn</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleShare("bluesky")}
                        className="h-8 gap-1.5 px-2.5 text-white/70 hover:bg-white/10 hover:text-white"
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z" />
                        </svg>
                        <span className="hidden sm:inline">Bluesky</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleShare("email")}
                        className="h-8 gap-1.5 px-2.5 text-white/70 hover:bg-white/10 hover:text-white"
                      >
                        <Mail className="h-4 w-4" />
                        <span className="hidden sm:inline">Email</span>
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
              {isSignedIn && isExplorerPlan(plan) && (
                <Card className="border border-white/10 bg-black/30">
                  <CardHeader>
                    <CardTitle className="text-lg text-white">How the Explorer limit works</CardTitle>
                    <CardDescription className="text-white/70">
                      Your free CodeRide Explorer account includes {MONTHLY_EXPLORER_LIMIT} PRD exports per month. Upgrade to Creator or above for unlimited documentation,
                      more AI tasks, and workflow automation.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <a
                      href="/pricing"
                      className="text-sm font-medium text-coderide-light-blue underline-offset-4 hover:underline"
                    >
                      Compare CodeRide plans →
                    </a>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </section>

        <section
          className="border-t border-white/0 bg-gradient-to-b from-transparent via-[#050507]/40 to-[#03040d] py-20"
          aria-labelledby="prd-playbooks"
        >
          <div className="container space-y-8">
            <div className="space-y-3 text-center">
              <Badge variant="outline" className="border-coderide-light-blue/40 text-coderide-light-blue">
                PRD playbooks
              </Badge>
              <h2 id="prd-playbooks" className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                Guides for every PRD workflow
              </h2>
              <p className="text-sm text-white/60">
                Whether you&apos;re explaining what a PRD is or orchestrating AI-first build steps, use these tabs to navigate the right playbook.
              </p>
            </div>
            <Tabs defaultValue="workflow" className="space-y-8">
              <TabsList className="mx-auto flex w-full max-w-xl flex-wrap items-center justify-center gap-3 bg-transparent">
                <TabsTrigger
                  value="workflow"
                  className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white/55 transition data-[state=active]:bg-black data-[state=active]:text-white"
                >
                  <Sparkles className="h-4 w-4" /> Workflow guide
                </TabsTrigger>
                <TabsTrigger
                  value="basics"
                  className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white/55 transition data-[state=active]:bg-black data-[state=active]:text-white"
                >
                  <BookOpen className="h-4 w-4" /> PRD basics
                </TabsTrigger>
              </TabsList>

              <TabsContent value="workflow" className="space-y-6">
                <div className="grid gap-8 lg:grid-cols-[1.1fr,0.9fr]">
                  <div className="space-y-4 text-white">
                    <Badge variant="outline" className="border-white/30 text-white/80">
                      PRD workflow guide
                    </Badge>
                    <h3 className="text-3xl font-semibold sm:text-4xl">Create polished PRDs in minutes</h3>
                    <p className="text-white/70">
                      Turn ideas, notes, or research docs into a structured PRD. The generator links every field to CodeRide tasks so Cursor, Claude, or teammates stay in sync.
                    </p>
                    <p className="text-sm text-white/60">
                      Use it whether you need to explain what a PRD is, fill a template for finance or ops, or hand structured specs to an AI coding stack.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {AI_SEARCH_KEYWORDS.map((keyword) => (
                        <span
                          key={keyword}
                          className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white/80"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                    <Button variant="primary" asChild className="mt-2 w-fit">
                      <a
                        href="#prd-wizard"
                        onClick={() => trackCtaClick("playbooks-start")}
                      >
                        Start a PRD
                      </a>
                    </Button>
                  </div>
                  <div className="rounded-3xl border border-white/10 bg-black/70 p-6 text-white shadow-[0_35px_90px_-45px_rgba(5,8,20,0.8)]">
                    <p className="text-xs font-semibold uppercase tracking-[0.4em] text-white/50">Workflow highlights</p>
                    <ul className="mt-5 space-y-3 text-sm text-white/80">
                      <li className="flex items-start gap-2">
                        <Sparkles className="mt-0.5 h-4 w-4 text-coderide-light-blue" />
                        <span>Guided chat + manual editing keeps MCP context aligned with every update.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 text-[#5eead4]" />
                        <span>Markdown exports drop into Notion, Linear, or GitHub with zero formatting work.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Layers className="mt-0.5 h-4 w-4 text-[#c2b5ff]" />
                        <span>Every section maps to CodeRide tasks so implementation and AI agents stay coordinated.</span>
                      </li>
                    </ul>
                    <div className="mt-6 space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-[0.35em] text-white/50">AI coding stacks</p>
                      <div className="flex flex-wrap items-center gap-3">
                        {AI_TOOL_LOGOS.map((tool) => (
                          <div key={tool.name} className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5">
                            <img src={tool.logo} alt={tool.alt} className="h-7 w-7 rounded-full border border-white/5 bg-white/5 object-contain" loading="lazy" />
                            <span className="text-sm font-medium text-white/80">{tool.name}</span>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-white/60">Works with Cursor, Claude, Gemini, Windsurf, Copilot, VS Code, and more.</p>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="basics" className="space-y-6">
                <div className="grid gap-8 lg:grid-cols-[1.1fr,0.9fr]">
                  <div className="space-y-4 text-white">
                    <Badge variant="outline" className="border-white/30 text-white/80">
                      PRD basics
                    </Badge>
                    <h3 className="mt-4 text-3xl font-semibold sm:text-4xl">What is a PRD?</h3>
                    <p className="mt-4 text-white/75">
                      A <strong>Product Requirements Document (PRD)</strong> captures the problem, target audience, and success metrics. It translates vision into a buildable plan that product, engineering, and AI copilots can follow.
                    </p>
                    <p className="mt-3 text-sm text-white/60">
                      A solid PRD covers context, personas, stories, feature requirements, metrics, launch plans, and known risks. Locking those details upfront keeps Cursor, Claude, and teammates aligned.
                    </p>
                    <Button variant="primary" asChild className="mt-6 w-fit">
                      <a href="https://coderide.ai/blog/what-is-a-prd-why-it-matters-ai-projects" target="_blank" rel="noreferrer">
                        Read the full guide
                      </a>
                    </Button>
                  </div>
                  <div className="rounded-3xl border border-white/10 bg-black/70 p-6 text-white shadow-[0_35px_90px_-45px_rgba(5,8,20,0.8)]">
                    <p className="text-xs font-semibold uppercase tracking-[0.4em] text-white/50">Why it matters for AI workflows</p>
                    <ul className="mt-5 space-y-3 text-sm text-white/80">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 text-[#5eead4]" />
                        <span>PRDs act as a shared contract between product, engineering, and AI copilots.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Layers className="mt-0.5 h-4 w-4 text-[#c2b5ff]" />
                        <span>Every section maps to CodeRide tasks, keeping tickets, prompts, and QA artifacts in sync.</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Sparkles className="mt-0.5 h-4 w-4 text-coderide-light-blue" />
                        <span>AI-generated PRDs give you an editable starting point so you spend time refining strategy.</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </section>
        <section
          className="border-t border-white/0 bg-gradient-to-b from-transparent via-[#050507]/35 to-[#050507] py-20"
          aria-labelledby="prd-research"
        >
          <div className="relative isolate">
            <MagicBgPattern size={64} fill="rgba(120,103,238,0.22)" />
            <div className="container relative z-10 space-y-16">
            <div className="mx-auto max-w-3xl text-center space-y-6">
              <Badge variant="outline" className="border-[#7867EE]/40 text-[#7867EE]">
                Why PRDs matter
              </Badge>
              <h2 id="prd-research" className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
                Structured requirements supercharge vibe coding and AI-assisted development
              </h2>
              <p className="text-lg text-white/70">
                Teams that pair PRDs with AI tools deliver faster without sacrificing quality. Here&apos;s what the latest research says about specification-driven development.
              </p>
              <div className="grid gap-4 text-left sm:grid-cols-3">
                {PRD_VISIBILITY_SUBHEADS.map((item) => (
                  <div key={item.title} className="rounded-2xl border border-white/10 bg-black/30 p-4 text-left">
                    <h3 className="text-base font-semibold text-white">{item.title}</h3>
                    <p className="mt-2 text-sm text-white/65">{item.copy}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="pointer-events-none absolute inset-0 rounded-[48px] bg-gradient-to-br from-coderide-light-blue/10 via-transparent to-[#63d7ff]/5 blur-3xl" />
              <div className="relative grid gap-6 lg:grid-cols-3">
                <div className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 shadow-[0_30px_60px_-40px_rgba(6,8,20,0.6)] backdrop-blur">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-[#7867EE]/25">
                        <Sparkles className="relative h-5 w-5 text-[#c2b5ff]" />
                      </div>
                      <p className="text-xs font-semibold uppercase tracking-[0.35em] text-white/50">Accuracy</p>
                    </div>
                    <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/70">Specs → Precision</span>
                  </div>
                  <h3 className="mt-6 text-lg font-semibold text-white">Fewer iterations, better code</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/75">
                    Structured specs boost functional correctness, testing, and modelling by up to{" "}
                    <span className="font-semibold text-white">32%</span>, raise unit-test pass rates by{" "}
                    <span className="font-semibold text-white">53%</span>, and improve traceability by{" "}
                    <span className="font-semibold text-white">23.9%</span>.
                  </p>
                  <ul className="mt-5 space-y-2.5 text-sm text-white/70">
                    <li className="flex items-start gap-3">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#c2b5ff]" />
                      <span><strong className="text-white">Complete context</strong> and acceptance criteria reduce prompt churn.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#c2b5ff]" />
                      <span><strong className="text-white">Edge cases + examples</strong> stop hallucinated behaviour before it starts.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#c2b5ff]" />
                      <span>Traceable requirements help reviewers understand every change in seconds.</span>
                    </li>
                  </ul>
                </div>

                <div className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 shadow-[0_30px_60px_-40px_rgba(6,8,20,0.6)] backdrop-blur">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-[#63d7ff]/20">
                        <Cpu className="relative h-5 w-5 text-[#63d7ff]" />
                      </div>
                      <p className="text-xs font-semibold uppercase tracking-[0.35em] text-white/50">Velocity</p>
                    </div>
                    <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/70">Specs → Speed</span>
                  </div>
                  <h3 className="mt-6 text-lg font-semibold text-white">Ship faster with confidence</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/75">
                    AI coding assistants paired with PRDs help teams complete{" "}
                    <span className="font-semibold text-white">26%</span> more tasks, cut pull-request cycles by{" "}
                    <span className="font-semibold text-white">39%</span>, and deliver features{" "}
                    <span className="font-semibold text-white">39%</span> faster.
                  </p>
                  <ul className="mt-5 space-y-2.5 text-sm text-white/70">
                    <li className="flex items-start gap-3">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#90e8ff]" />
                      <span><strong className="text-white">Clear specs</strong> reduce rework and code review friction.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#90e8ff]" />
                      <span>Reusable documentation becomes a launchpad for follow-on features.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#90e8ff]" />
                      <span>Onboarding speed improves with one source of truth for requirements.</span>
                    </li>
                  </ul>
                </div>

                <div className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 shadow-[0_30px_60px_-40px_rgba(6,8,20,0.6)] backdrop-blur">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-[#5eead4]/20">
                        <ShieldCheck className="relative h-5 w-5 text-[#5eead4]" />
                      </div>
                      <p className="text-xs font-semibold uppercase tracking-[0.35em] text-white/50">Quality</p>
                    </div>
                    <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/70">Specs → Resilience</span>
                  </div>
                  <h3 className="mt-6 text-lg font-semibold text-white">Reduce bugs and production risk</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/75">
                    Teams with specification-first workflows report cleaner diffs, fewer incidents, and higher code review acceptance. Test suites generated from documentation compile successfully{" "}
                    <span className="font-semibold text-white">100%</span> of the time.
                  </p>
                  <ul className="mt-5 space-y-2.5 text-sm text-white/70">
                    <li className="flex items-start gap-3">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#8ff5dc]" />
                      <span><strong className="text-white">Acceptance criteria</strong> automate validation and testing.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#8ff5dc]" />
                      <span>Documentation keeps security &amp; compliance in sight for reviewers.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#8ff5dc]" />
                      <span>Non-functional requirements stay front and centre before launch.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <div className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 backdrop-blur">
                <h3 className="text-xl font-semibold text-white">What makes a high-impact PRD?</h3>
                <p className="mt-3 text-sm text-white/70">
                  Research identifies six components every effective spec should include. CodeRide&apos;s guided brief helps you collect all of them by default.
                </p>
                <div className="mt-6 grid gap-4 text-sm text-white/65 sm:grid-cols-2">
                  {["Context", "Functional requirements", "Edge cases & constraints", "Examples", "Acceptance criteria", "Non-functional concerns"].map((label, index) => (
                    <div key={label} className="flex items-start gap-3 rounded-xl border border-white/10 bg-[#0c0d18] p-3">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/10 text-xs font-semibold text-white/70">
                        {index + 1}
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-white">{label}</p>
                        <p className="text-xs text-white/60">
                          {index === 0 && "Explain why the work matters and how it fits the roadmap."}
                          {index === 1 && "Spell out explicit behaviour, inputs, outputs, and domain rules."}
                          {index === 2 && "Call out boundaries the AI will never infer on its own."}
                          {index === 3 && "Provide concrete input/output examples for vibe coding prompts."}
                          {index === 4 && "Define the automated gates that signal done."}
                          {index === 5 && "Capture performance, security, compliance, and support needs."}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 backdrop-blur">
                <h3 className="text-xl font-semibold text-white">Why vibe coders still need docs</h3>
                <p className="mt-3 text-sm text-white/70">
                  Vibe coding isn&apos;t about ditching structure — it&apos;s about keeping your momentum while quality gates run silently in the background.
                </p>
                <div className="mt-6 space-y-4">
                  {WHY_DOCS_POINTS.map(({ title, copy, icon: Icon, chipClass }) => (
                    <div key={title} className="flex items-start gap-3 rounded-xl border border-white/10 bg-[#0c0d18] p-3">
                      <div className={cn("mt-0.5 flex h-7 w-7 items-center justify-center rounded-full border", chipClass)}>
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">{title}</p>
                        <p className="text-xs text-white/60">{copy}</p>
                      </div>
                    </div>
                  ))}
                  <p className="text-sm text-white/65">
                    The result? Vibe coding that delivers production-ready features instead of piles of follow-up tickets.
                  </p>
                </div>
              </div>
            </div>

            <div className="mx-auto max-w-3xl rounded-2xl bg-white/5 p-6 text-center text-sm text-white/60 ring-1 ring-white/10">
              <p>
                “Teams that adopted specification-first AI development saw feature delivery speed rise <strong className="text-white">39%</strong> while production incidents fell — because every build started from a living PRD.” <span className="text-white/40">— Qodo State of AI Code Quality, 2025</span>
              </p>
            </div>
            </div>
          </div>
        </section>

        <section
          className="border-t border-white/10 bg-[#050507] py-20"
          aria-labelledby="ai-prd-faq"
        >
          <div className="container space-y-10">
            <div className="space-y-3 text-center">
              <Badge variant="outline" className="border-white/15 text-white/70">
                PRD FAQ
              </Badge>
              <h2 id="ai-prd-faq" className="text-3xl font-semibold text-white">
                Frequently asked questions about PRDs
              </h2>
              <p className="text-sm text-white/60">
                Here you can find the right answer whether you searched for PRD meaning, templates, software, or AI workflows.
              </p>
            </div>

            <div className="grid gap-8 lg:grid-cols-[240px,1fr]">
              <div className="rounded-3xl bg-white/5 p-4 ring-1 ring-white/10">
                <div className="flex flex-wrap gap-3 lg:flex-col">
                  {FAQ_CATEGORIES.map((category) => {
                    const Icon = category.icon;
                    const isActive = category.value === activeFaqCategory;
                    return (
                      <button
                        key={category.value}
                        type="button"
                        onClick={() => setActiveFaqCategory(category.value)}
                        className={cn(
                          "flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left transition hover:border-white/30",
                          isActive ? "border-white/40 bg-white/10 text-white" : "border-white/10 text-white/70",
                        )}
                        aria-pressed={isActive}
                      >
                        <div className="flex items-center gap-3">
                          <span className={cn("flex h-8 w-8 items-center justify-center rounded-full border text-xs font-semibold uppercase tracking-wide", category.chipClass)}>
                            <Icon className="h-4 w-4" aria-hidden="true" />
                          </span>
                          <span className="font-semibold">{category.label}</span>
                        </div>
                        <span className="text-xs text-white/50">{AI_FAQ_ITEMS.filter((item) => item.category === category.value).length}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-5 rounded-3xl bg-white/5/80 p-6 shadow-[0_25px_80px_-50px_rgba(5,8,20,1)]">
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.35em] text-white/50">
                    {activeFaqCategoryMeta.label}
                  </p>
                  <h3 className="text-2xl font-semibold text-white">
                    Answers for {activeFaqCategoryMeta.label.toLowerCase()}
                  </h3>
                  <p className="text-sm text-white/60">
                    Select a question to see a clear explanation plus next steps inside the generator.
                  </p>
                </div>
                <Accordion type="single" collapsible className="space-y-4">
                  {visibleFaqItems.map((item, index) => {
                    const categoryConfig = FAQ_CATEGORIES.find((category) => category.value === item.category);
                    return (
                      <AccordionItem
                        key={item.question}
                        value={`${item.category}-${index}`}
                        className="overflow-hidden rounded-2xl border-0 bg-white/5/70 px-4 shadow-[0_10px_35px_-28px_rgba(0,0,0,1)]"
                      >
                        <AccordionTrigger className="flex w-full items-center justify-between gap-6 text-left text-base font-semibold text-white hover:text-white hover:no-underline">
                          <span className="flex-1">{item.question}</span>
                          <span
                            className={cn(
                              "flex min-w-[190px] items-center justify-center rounded-full border px-3 py-1 text-center text-xs font-semibold uppercase tracking-wide hover:no-underline",
                              categoryConfig?.chipClass ?? "border-white/15 text-white/70",
                            )}
                          >
                            {categoryConfig?.label ?? "FAQ"}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="space-y-3 text-sm text-white/70">
                          <p>{item.answer}</p>
                          {item.cta && (
                            <a
                              href={item.cta.href}
                              className="inline-flex items-center gap-2 text-coderide-light-blue hover:text-white hover:no-underline"
                            >
                              {item.cta.label}
                              <ArrowUpRight className="h-4 w-4" />
                            </a>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </div>
            </div>
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_SCHEMA) }} />
          </div>
        </section>

        <section
          className="border-t border-white/5 bg-[#04050a] py-12"
          aria-labelledby="related-ai-task-manager"
        >
          <div className="container">
            <div className="rounded-3xl border border-white/10 bg-black/30 px-6 py-8 text-center shadow-[0_20px_80px_-40px_rgba(6,9,20,1)]">
              <p id="related-ai-task-manager" className="text-lg font-semibold text-white">
                Learn more about how CodeRide turns PRDs into AI-optimized tasks →
              </p>
              <a
                href="/#features"
                className="mt-4 inline-flex items-center justify-center gap-2 text-base font-semibold text-coderide-light-blue underline-offset-4 hover:text-white"
              >
                CodeRide AI Task Manager
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default PRDGenerator;
